<script>
import axios from "axios";
import Vue from "vue";
import VueSignaturePad from "vue-signature-pad";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import SocketioService from "@/services/socketio.service.js";
import CheckinEditDrawer from "@/components/Checkins/CheckinEditDrawer.vue";
import { guestFlagUrl } from "@/utils/mrzNationality";
import * as listPaises from "@/config/webpol";
import daneCountriesData from "@/utils/code_countries.json";
import daneCitiesData from "@/utils/code_province.json";
import Multiselect from "vue-multiselect";
// 🇮🇹 Alloggiati Web (Polizia di Stato) — códigos oficiales de 9 cifras
import { ALLOGGIATI_STATI, ALLOGGIATI_ITALIA_CODE } from "@/utils/alloggiatiStati";
import { ALLOGGIATI_COMUNI } from "@/utils/alloggiatiComuni";
// Directorio de Clientes: reutilizar a quien ya hizo check-in antes.
import { buscarClientes, datosParaRellenar, coincidenciaCliente } from "@/services/clientesDirectorio";

Vue.use(VueSignaturePad);

// País de residencia (SES Hospedajes): lista COMPLETA de países con código ISO3,
// la misma que usa "Nacionalidad" en este form y el proceso de check-in del
// huésped (webpol). Antes era una lista corta hardcodeada y faltaban países
// (p. ej. Venezuela).
const PAISES_RESIDENCIA = (listPaises.nacionalidades || []).map((p) => ({
  code: p.code,
  nombre: p.name,
}));

// Códigos de parentesco (los labels se resuelven por i18n en relationshipOptions).
const COLOMBIA_RELATIONSHIPS = [
  "PADRE", "MADRE", "HIJO", "ABUELO", "HERMANO", "TIO", "TUTOR", "OTRO",
];

const SPAIN_RELATIONSHIPS = [
  "HJ", "AB", "BA", "BN", "CD", "CY", "HR", "NI", "OT", "PM", "SB", "SG", "TI", "TU", "YN",
];

const DEFAULT_FORM = () => ({
  nombre: "",
  apellido: "",
  sex: "",
  date_of_birth: "",
  phone: "",
  email: "",
  fecha_ingreso: "",
  fecha_salida: "",
  pais: null,
  type_documento: "",
  nu_documento: "",
  document_support: "",
  emision: null,
  relationship: null,
  existChildren: "",   // "si" | "no" — adultos que viajan con menores
  // Spain — SES Hospedajes
  country_residence: "",
  city_residence: "",
  address: "",
  zip_code: "",
  type_payment_checkin: "",
  payment_date_checkin: "",
  // Italia — Alloggiati Web (Polizia di Stato)
  paese_nascita:        null,  // stato di nascita { code(9), nome, es, en, iso2, iso3 }
  luogo_nascita:        "",    // código 9 cifras del comune (solo nacidos en Italia)
  luogo_rilascio_doc:   "",    // código 9 cifras (comune o stato de emisión del documento)
  // Colombia — SIRE/TRA (campos exactos del modelo Cliente)
  sire_nacionalidad:            "",    // código DANE de la nacionalidad (Colombia usa DANE, no webpol)
  sire_nacionalidad_nombre:     "",    // nombre para display
  sire_procedencia:             "",    // código DANE país de procedencia (4 dígitos)
  sire_procedencia_nombre:      "",    // nombre para display
  procedencia_municipio_code:   "",    // código DANE municipio (solo si procedencia = CO)
  procedencia_municipio_nombre: "",    // nombre municipio
  sire_destino:                 "169", // código DANE país próximo destino (default CO)
  sire_destino_nombre:          "COLOMBIA",
});

export default {
  name: "CheckinList",
  components: {
    CheckinEditDrawer,
    VueTelInput,
    Multiselect,
  },
  // Portal: mueve el elemento a <body>. El modal "Nuevo check-in" es
  // position:fixed pero se renderiza dentro del detalle de reserva (drawer/modal),
  // cuyo ancestro con transform/overflow lo ATRAPABA → no se veía. Al portarlo a
  // body escapa de ese contexto de apilamiento y aparece centrado como overlay.
  directives: {
    portalBody: {
      inserted(el) {
        el.__portalHome = el.parentNode;
        document.body.appendChild(el);
      },
      unbind(el) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      },
    },
  },
  props: {
    reservationId: {
      type: [Number, String],
      required: true,
    },
    totalGuests: {
      type: [Number, String],
      default: 0,
    },
    initialCheckins: {
      type: Array,
      default: () => [],
    },
    property: {
      type: Object,
      default: () => ({}),
    },
    reservationData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      checkins: [],
      showEditDrawer: false,
      editingCheckin: null,

      // Modal
      showModal: false,
      modalStep: 1,
      modalSaving: false,
      form: DEFAULT_FORM(),
      termsAccepted: false,

      // Spain — postal code
      municipio_code: null,
      municipio_nombre: "",
      postalData: [],

      // Nationality list (webpol)
      paisesNacionalidad: listPaises.nacionalidades || [],
      paisesResidencia: PAISES_RESIDENCIA,

      // Colombia — DANE data
      daneCountries: daneCountriesData || [],
      daneCities: daneCitiesData || [],

      // Colombia — SIRE searchable selects state
      // Nacionalidad
      sireNacSearch: "",
      showNacDropdown: false,
      nacPanelStyle: {},          // posición fixed calculada en toggle
      // País de procedencia (countries)
      sireProcCountrySearch: "",
      showProcCountryDropdown: false,
      procPanelStyle: {},
      // Ciudad de procedencia (municipalities searchable select)
      cityProcSearch: "",
      showProcDropdown: false,
      cityPanelStyle: {},
      // País de destino
      sireDestSearch: "",
      showDestDropdown: false,
      destPanelStyle: {},

      // Directorio de Clientes (buscar guardado, «ya existe», acompañantes)
      dirQuery: "",
      dirResults: [],
      dirOpen: false,
      dirLoading: false,
      dirTimer: null,
      dirMatch: null,          // persona encontrada mientras se escribe
      dirMatchTimer: null,
      dirMatchSeq: 0,
      dirDescartados: [],      // «No es esta persona»
      dirAplicado: null,       // { id, nombre } cuyos datos están en el formulario
      dirCompaneros: [],       // con quién viajó antes la persona aplicada
      dirRellenando: false,

      // Inline field errors
      docError:   "",
      emailError: "",

      // Italia — Alloggiati Web (objetos seleccionados en los multiselect)
      ALLOGGIATI_ITALIA_CODE,
      selCittadinanza:  null,  // stato → form.pais (cittadinanza ISO3)
      selStatoNascita:  null,  // stato → form.paese_nascita
      selComuneNascita: null,  // comune → form.luogo_nascita
      selLuogoRilascio: null,  // comune/stato → form.luogo_rilascio_doc

      // Photo uploads
      photoFront: null,
      photoSelfie: null,
      photoMinorAuth: null,
      photoFrontPreview: null,
      photoSelfiePreview: null,
      photoMinorAuthPreview: null,

      automationLoading: false,
      automationEnabled: false,
      manualDispatchLoading: false,
      manualDispatchSentAt: null,
      arrivalSendLoading: false,
    };
  },

  computed: {
    doneCount() {
      return this.checkins.length;
    },
    isCompleted() {
      return (
        this.totalGuests &&
        Number(this.doneCount) >= Number(this.totalGuests)
      );
    },
    hasPendingGuests() {
      return Number(this.totalGuests || 0) > 0 && Number(this.doneCount) < Number(this.totalGuests);
    },
    manualDispatchResolvedAt() {
      return this.manualDispatchSentAt || this.reservationData?.first_checkin_info_sent_at || null;
    },
    canManualDispatch() {
      // Acción MANUAL explícita: NO depende del switch "Envío final automático"
      // (ese switch solo controla el envío automático al completar todos).
      // Aparece con ≥1 check-in hecho y huéspedes pendientes; con 1 solo huésped
      // nunca aplica (hasPendingGuests sería false al completarlo).
      return (
        !this.manualDispatchResolvedAt &&
        this.doneCount > 0 &&
        this.hasPendingGuests
      );
    },
    activeProperty() {
      return this.property && this.property.id
        ? this.property
        : this.$store.state.property?.property || {};
    },
    isVisibleData() {
      return this.isSpain;
    },
    // País efectivo del check-in. El país se define a nivel anuncio (Plantilla),
    // pero según el endpoint puede llegar en `code_pais` o en `pais_code`, en el
    // objeto de la reserva o en el store. Comprobamos todas las fuentes para que
    // ES / CO / IT se detecten igual que en el proceso de check-in del huésped.
    propCountry() {
      const prop  = this.activeProperty || {};
      const store = this.$store.state.property?.property || {};
      const code =
        prop.code_pais        || prop.pais_code        ||
        prop.listing?.code_pais || prop.listing?.pais_code ||
        store.code_pais       || store.pais_code       ||
        "";
      return String(code).toUpperCase();
    },
    isSpain() {
      return this.propCountry === "ES" || this.propCountry === "ESP";
    },
    isColombia() {
      return this.propCountry === "CO" || this.propCountry === "COL";
    },
    isItaly() {
      return this.propCountry === "IT" || this.propCountry === "ITA";
    },
    // Nació en Italia → se pide el Comune di Nascita (código Alloggiati)
    isItalianBorn() {
      return this.form.paese_nascita?.code === ALLOGGIATI_ITALIA_CODE;
    },
    // Ciudadanía italiana → el documento se emite normalmente en un comune italiano
    isItalianCitizen() {
      const code = (this.form.pais?.code || "").toUpperCase();
      return code === "ITA" || code === "IT";
    },
    // Listado de Stati (Alloggiati) con etiqueta localizada, Italia primero
    statiOptions() {
      const list = ALLOGGIATI_STATI.map((s) => ({ ...s, label: this.statoLabel(s) }));
      list.sort((a, b) => {
        if (a.code === ALLOGGIATI_ITALIA_CODE) return -1;
        if (b.code === ALLOGGIATI_ITALIA_CODE) return 1;
        return a.label.localeCompare(b.label);
      });
      return list;
    },
    // Listado de Comuni (Alloggiati) con etiqueta "NOME (PROV)"
    comuniOptions() {
      return ALLOGGIATI_COMUNI.map((c) => ({ ...c, label: `${c.nome} (${c.provincia})` }));
    },
    // Cliente reutilizado del que no hay datos de policía guardados en esta cuenta.
    faltanDatosPolicia() {
      if (!this.dirAplicado) return false;
      if (this.isSpain) return !this.form.address && !this.form.country_residence;
      if (this.isColombia) return !this.form.sire_procedencia;
      if (this.isItaly) return !this.form.paese_nascita;
      return false;
    },
    // Acompañantes de antes que aún no tienen check-in en esta reserva.
    companerosPendientes() {
      const docs = new Set(
        (this.checkins || [])
          .map((c) => String(c.nu_documento || c.cliente?.nu_documento || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase())
          .filter(Boolean)
      );
      return (this.dirCompaneros || []).filter((a) => {
        const doc = String(a.nu_documento || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
        return !doc || !docs.has(doc);
      });
    },
    docTypes() {
      const dt = (k) => this.$t("reservasHc.checkinsList.docTypes." + k);
      const other = this.$t("reservasHc.checkinsList.other");
      if (this.isSpain) {
        return [
          { value: "ID",   label: "DNI" },
          { value: "IM",   label: "NIE" },
          { value: "P",    label: dt("passport") },
          { value: "OTRO", label: other },
        ];
      }
      if (this.isColombia) {
        return [
          { value: "CC",   label: dt("co_cc") },
          { value: "CE",   label: dt("co_ce") },
          { value: "PPT",  label: dt("co_ppt") },
          { value: "PEP",  label: dt("co_pep") },
          { value: "P",    label: dt("passport") },
          { value: "DE",   label: dt("co_de") },
          { value: "OTRO", label: other },
        ];
      }
      if (this.isItaly) {
        // Códigos oficiales Alloggiati Web (Polizia di Stato) — etiquetas oficiales en italiano
        return [
          { value: "IDEN", label: "Carta d'identità" },
          { value: "PASS", label: "Passaporto" },
          { value: "PATE", label: "Patente di guida" },
          { value: "RESI", label: "Permesso di soggiorno" },
          { value: "DEXT", label: "Documento straniero" },
        ];
      }
      return [
        { value: "ID",   label: dt("identityDoc") },
        { value: "P",    label: dt("passport") },
        { value: "OTRO", label: other },
      ];
    },
    isSpanishNationality() {
      return this.form.pais && this.form.pais.code === "ESP";
    },
    requiresDocSupport() {
      return (
        this.isSpain &&
        (this.form.type_documento === "ID" || this.form.type_documento === "IM") &&
        this.isSpanishNationality
      );
    },
    isMinor() {
      if (!this.form.date_of_birth) return false;
      const birth = new Date(this.form.date_of_birth);
      const now = new Date();
      const age = now.getFullYear() - birth.getFullYear();
      const m = now.getMonth() - birth.getMonth();
      return age < 18 || (age === 18 && m < 0);
    },
    // Adulto que marcó que viaja con menores
    requiresRelationship() {
      return this.isMinor || this.form.existChildren === "si";
    },
    requiresIdentityVerification() {
      // r_selfie no siempre viene en el prop (propiedad cargada con select parcial);
      // fallback al store que tiene el objeto completo de la propiedad activa.
      const rSelfie =
        this.property?.r_selfie ??
        this.$store.state.property?.property?.r_selfie;
      return !!(this.isColombia && rSelfie);
    },
    relationshipOptions() {
      const set = this.isSpain ? "es" : "co";
      const codes = this.isSpain ? SPAIN_RELATIONSHIPS : COLOMBIA_RELATIONSHIPS;
      return codes.map((value) => ({
        value,
        label: this.$t(`reservasHc.checkinsList.relationships.${set}.${value}`),
      }));
    },
    defaultCountryCode() {
      if (this.isSpain)    return "ES";
      if (this.isColombia) return "CO";
      if (this.isItaly)    return "IT";
      return "ES";
    },
    // true cuando el país de procedencia seleccionado es Colombia (código DANE 169)
    procedenciaEsColombia() {
      return this.form.sire_procedencia === "169";
    },
    filteredProcCities() {
      const q = (this.cityProcSearch || "").trim().toUpperCase();
      if (!q) return this.daneCities.slice(0, 100);
      return this.daneCities
        .filter((c) => c.nombre.toUpperCase().includes(q))
        .slice(0, 100);
    },
    // Searchable DANE country lists (Nac / Proc / Dest)
    filteredNacCountries() {
      const q = (this.sireNacSearch || "").trim().toUpperCase();
      if (!q) return this.daneCountries.slice(0, 120);
      return this.daneCountries.filter((c) => c.nombre.toUpperCase().includes(q)).slice(0, 120);
    },
    filteredProcCountries() {
      const q = (this.sireProcCountrySearch || "").trim().toUpperCase();
      if (!q) return this.daneCountries.slice(0, 120);
      return this.daneCountries.filter((c) => c.nombre.toUpperCase().includes(q)).slice(0, 120);
    },
    filteredDestCountries() {
      const q = (this.sireDestSearch || "").trim().toUpperCase();
      if (!q) return this.daneCountries.slice(0, 120);
      return this.daneCountries.filter((c) => c.nombre.toUpperCase().includes(q)).slice(0, 120);
    },
  },

  watch: {
    initialCheckins: {
      immediate: true,
      handler(val) {
        this.checkins = Array.isArray(val) ? [...val] : [];
      },
    },
    // Italia: el selector de "Luogo di rilascio" cambia entre comune (italiano)
    // y stato (extranjero) según la cittadinanza → al cambiar, limpiamos la
    // selección previa para no enviar un código del tipo equivocado.
    isItalianCitizen() {
      this.selLuogoRilascio = null;
      this.form.luogo_rilascio_doc = "";
    },
    // «Ya existe este cliente»: se pregunta al directorio mientras se escribe.
    "form.nu_documento"() { this.programarCoincidencia(); },
    "form.date_of_birth"() { this.programarCoincidencia(); },
    "form.apellido"() { this.programarCoincidencia(); },
    "form.phone"() { this.programarCoincidencia(); },
    "form.email"() { this.programarCoincidencia(); },
    reservationId: {
      immediate: true,
      async handler(newId) {
        if (!newId) return;
        await this.fetchCheckins();
        await this.loadAutomationSetting();
        this.subscribeSocket();
      },
    },
  },

  mounted() {
    this.subscribeSocket();
    if (this.isSpain) this.loadPostalData();
    // Cierra los searchable-selects SIRE al hacer clic fuera del componente
    document.addEventListener("click", this._onDocClickSire, true);
  },

  beforeDestroy() {
    this.unsubscribeSocket();
    document.body.style.overflow = "";
    document.removeEventListener("click", this._onDocClickSire, true);
  },

  methods: {
    async activeModalCheckin(item) {
      // Mostrar el modal de inmediato con los datos ya disponibles en el listado
      this.$store.state.datosModal = { ...item };
      this.$store.state.efecto = true;
      window.$("#modalDatosGeneral").modal("show");

      try {
        // Obtener datos completos (incluyendo identity_photos) desde el mismo
        // endpoint que usa el listado global de check-ins
        const { data } = await axios.post(
          this.$base.dominio + "/retrive-checkin",
          {
            draw: 1,
            length: 50,
            column: 0,
            dir: "desc",
            search: item.nu_documento || "",
            user_id: this.$store.state.userRoot?.id,
            type: this.$store.state.userRoot?.type,
            propiedadActiva: this.property?.id || this.$store.state.propiedadActiva,
          }
        );

        const items = data?.data?.data || [];
        // Buscar el checkin exacto por ID
        const full = items.find((c) => c.id === item.id);
        if (full) {
          this.$store.state.datosModal = full;
        }
      } catch (e) {
        // Fallback: quedarse con los datos del listado (ya están en el store)
        console.warn("[CheckinsList] No se pudo cargar identity_photos:", e);
      } finally {
        this.$store.state.efecto = false;
      }
    },

    // Las banderas se llaman por ISO2 y el escáner guarda ISO3. El substr(0,2)
    // que había aquí acertaba de casualidad (ESP→ES) y fallaba en el resto:
    // DNK→"DN" no existe y CHL→"CH" pintaba Suiza. Ver src/utils/mrzNationality.js
    guestFlagUrl,

    async fetchCheckins() {
      try {
        const { data } = await axios.get(
          `/reservations/${this.reservationId}/checkins`
        );
        this.checkins = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Error cargando checkins:", e);
      }
    },

    async loadAutomationSetting() {
      this.automationLoading = true;
      try {
        const { data } = await axios.get("/system-actions/first-checkin-send");
        this.automationEnabled = Boolean(data?.data?.is_enabled);
      } catch (error) {
        this.automationEnabled = false;
        console.error("Error cargando automatización de check-in:", error);
      } finally {
        this.automationLoading = false;
      }
    },

    subscribeSocket() {
      if (!SocketioService.socket) return;
      const user = this.$store.state.userRoot;
      if (!user || !user.id) return;
      this.unsubscribeSocket();
      SocketioService.socket.on(`chat-message-${user.id}`, this.handleSocketUpdate);
    },

    unsubscribeSocket() {
      if (!SocketioService.socket) return;
      const user = this.$store.state.userRoot;
      if (!user || !user.id) return;
      SocketioService.socket.off(`chat-message-${user.id}`, this.handleSocketUpdate);
    },

    async handleSocketUpdate(payload) {
      if (!payload || payload.type !== 4) return;
      if (
        payload.reservation_id &&
        String(payload.reservation_id) !== String(this.reservationId)
      ) return;
      await this.fetchCheckins();
    },

    openEditDrawer(item) {
      this.editingCheckin = item;
      this.showEditDrawer = true;
    },

    handleCloseDrawer() {
      this.showEditDrawer = false;
      this.editingCheckin = null;
    },

    async triggerManualDispatch() {
      const confirmado = await this.$wcConfirm({
        title: this.$t("reservasHc.checkinsList.confirmManualTitle"),
        message: this.$t("reservasHc.checkinsList.confirmManualMsg"),
        confirmText: this.$t("reservasHc.checkinsList.yesSendNow"),
        cancelText: this.$t("reservasHc.checkinsList.cancel"),
        variant: "warning",
      });

      if (!confirmado) {
        return;
      }

      this.manualDispatchLoading = true;
      try {
        const response = await axios.post("/system-actions/first-checkin-send/manual-dispatch", {
          reservation_id: this.reservationId,
        });
        const payload = response?.data || {};
        const sentAt = payload?.data?.sent_at || new Date().toISOString();

        if (!payload.success) {
          throw new Error(payload.message || this.$t("reservasHc.checkinsList.manualSendFailed"));
        }

        this.manualDispatchSentAt = sentAt;
        this.$emit("system-action-triggered", {
          sent_at: sentAt,
          result: payload?.data?.result || null,
        });

        await this.$wcAlert({
          variant: "success",
          title: this.$t("reservasHc.checkinsList.sendProcessedTitle"),
          message: payload.message || this.$t("reservasHc.checkinsList.finalInfoProcessed"),
        });
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("reservasHc.checkinsList.manualSendFailed");

        await this.$wcAlert({
          variant: "danger",
          title: this.$t("reservasHc.checkinsList.couldNotSend"),
          message,
        });
      } finally {
        this.manualDispatchLoading = false;
      }
    },

    // Envío MANUAL de las instrucciones de llegada por el operador ("enviar ahora"),
    // sin esperar a la hora programada. Incluye adjuntos y OTA si están activos.
    async triggerArrivalSend() {
      const already = !!this.reservationData?.arrival_sent_at;
      const confirmed = await this.$wcConfirm({
        title: already
          ? this.$t("reservasHc.checkinsList.confirmResendArrivalTitle")
          : this.$t("reservasHc.checkinsList.confirmSendArrivalTitle"),
        message: already
          ? this.$t("reservasHc.checkinsList.confirmResendArrivalMsg")
          : this.$t("reservasHc.checkinsList.confirmSendArrivalMsg"),
        confirmText: this.$t("reservasHc.checkinsList.yesSendNow"),
        cancelText: this.$t("reservasHc.checkinsList.cancel"),
        variant: "info",
      });
      if (!confirmed) return;

      this.arrivalSendLoading = true;
      try {
        const response = await axios.post("/system-actions/arrival-send/manual-dispatch", {
          reservation_id: this.reservationId,
          force: already,
        });
        const payload = response?.data || {};
        if (!payload.success) {
          throw new Error(payload.message || this.$t("reservasHc.checkinsList.arrivalSendFailed"));
        }
        this.$emit("system-action-triggered", {
          arrival_sent_at: payload?.data?.arrival_sent_at || new Date().toISOString(),
        });
        // Encolado NO es lo mismo que entregado: cuando la ventana de WhatsApp está
        // cerrada el mensaje queda esperando, y decirle «enviadas» en verde haría
        // creer al anfitrión que el huésped ya las tiene.
        const encolado = !!payload?.data?.queued;

        await this.$wcAlert({
          variant: encolado ? "info" : "success",
          title: encolado
            ? this.$t("reservasHc.checkinsList.pendingDeliveryTitle")
            : this.$t("reservasHc.checkinsList.instructionsSentTitle"),
          message: payload.message || this.$t("reservasHc.checkinsList.arrivalSentToGuest"),
        });
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("reservasHc.checkinsList.arrivalSendFailed");
        await this.$wcAlert({
          variant: "danger",
          title: this.$t("reservasHc.checkinsList.couldNotSend"),
          message,
        });
      } finally {
        this.arrivalSendLoading = false;
      }
    },

    handleSavedCheckin(updated) {
      if (!updated) return;
      const index = this.checkins.findIndex((c) => c.id === updated.id);
      if (index !== -1) {
        this.$set(this.checkins, index, {
          ...this.checkins[index],
          ...updated,
          cliente: updated.cliente
            ? { ...this.checkins[index].cliente, ...updated.cliente }
            : this.checkins[index].cliente,
        });
      }
      this.handleCloseDrawer();
    },

    eliminarCheckin(item, index) {
      this.$swal({
        title: this.$t("empresa.listbookings.p14"),
        text: this.$t("empresa.listbookings.p15"),
        icon: "warning",
        dangerMode: true,
        showCancelButton: true,
        cancelButtonColor: "gray",
        confirmButtonColor: "green",
        confirmButtonText: this.$t("empresa.listbookings.p16"),
        cancelButtonText: this.$t("empresa.listbookings.p17"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.$store.state.loader = true;
          axios
            .post(this.$base.dominio + "/delete/checkin", {
              historial_id: item.id,
              user_id: this.$store.state.userRoot.id,
            })
            .then(() => {
              this.checkins.splice(index, 1);
              this.$store.state.loader = false;
            })
            .catch((e) => {
              console.error(e);
              this.$store.state.loader = false;
            });
        }
      });
    },

    // ── Modal ────────────────────────────────────────────────────────────────
    openModal() {
      const rd = this.reservationData || {};

      // Formato de fechas: "YYYY-MM-DD" — ya viene así del backend, moment lo
      // normaliza igualmente por si viene con hora ("2026-05-04 00:00:00").
      const toDateInput = (val) =>
        val ? this.moment(String(val).slice(0, 10)).format("YYYY-MM-DD") : "";

      this.form = {
        ...DEFAULT_FORM(),
        // Solo prefill fechas — cada huésped del grupo tiene sus propios datos personales
        fecha_ingreso: toDateInput(rd.fecha_ingreso),
        fecha_salida:  toDateInput(rd.fecha_salida),
      };
      this.municipio_code = null;
      this.municipio_nombre = "";
      this.termsAccepted = false;
      this.modalStep = 1;
      this.modalSaving = false;
      this.docError   = "";
      this.emailError = "";
      // Reset SIRE searchable selects
      this.sireNacSearch = "";
      this.showNacDropdown = false;
      this.sireProcCountrySearch = "";
      this.showProcCountryDropdown = false;
      this.cityProcSearch = "";
      this.showProcDropdown = false;
      this.sireDestSearch = "";
      this.showDestDropdown = false;
      // Reset selectores Italia (Alloggiati)
      this.selCittadinanza  = null;
      this.selStatoNascita  = null;
      this.selComuneNascita = null;
      this.selLuogoRilascio = null;
      // Reset photos
      this.photoFront = null;
      this.photoSelfie = null;
      this.photoMinorAuth = null;
      this.photoFrontPreview = null;
      this.photoSelfiePreview = null;
      this.photoMinorAuthPreview = null;
      this.resetDirectorio();
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },

    // ── Directorio de Clientes ───────────────────────────────────────────────
    resetDirectorio() {
      clearTimeout(this.dirTimer);
      clearTimeout(this.dirMatchTimer);
      this.dirMatchSeq++;
      this.dirQuery = "";
      this.dirResults = [];
      this.dirOpen = false;
      this.dirLoading = false;
      this.dirMatch = null;
      this.dirDescartados = [];
      this.dirAplicado = null;
      this.dirCompaneros = [];
    },

    buscarEnDirectorio() {
      clearTimeout(this.dirTimer);
      const q = (this.dirQuery || "").trim();
      if (q.length < 2) {
        this.dirResults = [];
        this.dirOpen = false;
        return;
      }
      this.dirTimer = setTimeout(async () => {
        this.dirLoading = true;
        this.dirOpen = true;
        try {
          const items = await buscarClientes(q);
          if ((this.dirQuery || "").trim() === q) this.dirResults = items;
        } catch (e) {
          this.dirResults = [];
        } finally {
          this.dirLoading = false;
        }
      }, 250);
    },

    cerrarDirConPausa() {
      setTimeout(() => { this.dirOpen = false; }, 150);
    },

    async elegirCliente(id) {
      this.dirOpen = false;
      try {
        const persona = await datosParaRellenar(id);
        if (persona) this.aplicarCliente(persona);
      } catch (e) {
        toastr.error(this.$t("clientesDir.sheet.loadError"));
      }
    },

    /** Abre el alta manual ya rellena con una persona (acompañante de un toque). */
    openModalConCliente(id) {
      this.openModal();
      this.elegirCliente(id);
    },

    programarCoincidencia() {
      if (!this.showModal || this.modalStep !== 1 || this.dirRellenando) return;
      clearTimeout(this.dirMatchTimer);
      this.dirMatchTimer = setTimeout(() => this.buscarCoincidencia(), 600);
    },

    async buscarCoincidencia() {
      const f = this.form;
      const seq = ++this.dirMatchSeq;
      const params = {
        nu_documento:     (f.nu_documento || "").trim() || undefined,
        pais:             f.pais ? f.pais.code : undefined,
        telefono:         (f.phone || "").replace(/\s/g, "") || undefined,
        email:            (f.email || "").trim() || undefined,
        nombre:           (f.nombre || "").trim() || undefined,
        apellido:         (f.apellido || "").trim() || undefined,
        fecha_nacimiento: f.date_of_birth || undefined,
      };
      if (!params.nu_documento && !params.nombre) { this.dirMatch = null; return; }
      try {
        const persona = await coincidenciaCliente(params);
        if (seq !== this.dirMatchSeq || !this.showModal) return;
        if (
          persona &&
          (!this.dirAplicado || this.dirAplicado.id !== persona.id) &&
          !this.dirDescartados.includes(persona.id)
        ) {
          this.dirMatch = persona;
        } else if (!persona || (this.dirAplicado && this.dirAplicado.id === persona.id)) {
          this.dirMatch = null;
        }
      } catch (e) {
        // El aviso es una ayuda: si falla, el alta manual sigue igual.
      }
    },

    descartarCoincidencia() {
      if (this.dirMatch) this.dirDescartados.push(this.dirMatch.id);
      this.dirMatch = null;
    },

    aplicarCliente(persona) {
      this.dirRellenando = true;
      this.dirMatchSeq++;
      clearTimeout(this.dirMatchTimer);
      this.rellenarDesdeCliente(persona.datos || {});
      this.dirAplicado = { id: persona.id, nombre: persona.nombre_completo || "" };
      this.dirCompaneros = Array.isArray(persona.acompanantes) ? persona.acompanantes : [];
      this.dirMatch = null;
      this.dirQuery = "";
      this.dirResults = [];
      this.docError = "";
      this.emailError = "";
      this.$nextTick(() => {
        this.dirRellenando = false;
      });
      toastr.success(this.$t("clientesDir.match.applied", { name: this.dirAplicado.nombre }));
    },

    /**
     * Pasa al formulario lo guardado del cliente: datos personales, documento y los
     * campos de policía de cada país (SES, SIRE/TRA, Alloggiati). Las fechas de la
     * estancia NO se tocan: son las de esta reserva.
     */
    rellenarDesdeCliente(d) {
      const f = this.form;
      const has = (v) => v !== null && v !== undefined && String(v).trim() !== "";
      const set = (k, v) => { if (has(v)) this.$set(f, k, v); };

      set("nombre", d.nombre);
      set("apellido", d.apellido);
      if (has(d.sex)) {
        const sx = String(d.sex);
        const up = sx.toUpperCase();
        const opciones = ["M", "F", "Prefiero no decirlo", "Otro"];
        const valor = opciones.includes(sx) ? sx
          : ["MALE", "H", "HOMBRE", "MASCULINO"].includes(up) ? "M"
          : ["FEMALE", "MUJER", "FEMENINO"].includes(up) ? "F" : "";
        if (valor) f.sex = valor;
      }
      set("date_of_birth", d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : "");
      set("phone", d.whatsapp);
      set("email", d.email);

      // Nacionalidad (webpol, o Alloggiati en Italia)
      const code = String(d.code || d.pais_code || "").toUpperCase();
      if (code) {
        if (this.isItaly) {
          const st = this.statiOptions.find((s) => s.iso3 === code || s.iso2 === code);
          if (st) this.onItalyCittadinanza(st);
        } else {
          const p = this.paisesNacionalidad.find((x) => String(x.code).toUpperCase() === code);
          if (p) f.pais = p;
        }
      }

      if (has(d.type_documento)) {
        // El pasaporte se llama distinto según el país (P en España/Colombia, PASS en Italia).
        const tipo = String(d.type_documento).toUpperCase();
        const pasaporte = ["P", "PASS", "PASAPORTE", "PASSPORT"].includes(tipo);
        const candidatos = pasaporte ? [tipo, "P", "PASS"] : [d.type_documento];
        const elegido = candidatos.find((c) => this.docTypes.some((t) => t.value === c));
        if (elegido) f.type_documento = elegido;
      }
      set("nu_documento", d.nu_documento ? String(d.nu_documento).toUpperCase() : "");
      set("document_support", d.document_support);
      set("emision", d.emision);

      // España — SES Hospedajes
      set("country_residence", d.country_residence);
      set("city_residence", d.city_residence);
      set("address", d.address);
      set("zip_code", d.zip_code);
      if (has(d.municipio_code)) {
        this.municipio_code = d.municipio_code;
        this.municipio_nombre = d.municipio_nombre || "";
      }

      // Colombia — SIRE / TRA (códigos DANE)
      const dane = (c) => this.daneCountries.find((x) => String(x.codigo) === String(c));
      if (has(d.nacionalidad_code_num)) { const c = dane(d.nacionalidad_code_num); if (c) this.selectNacCountry(c); }
      if (has(d.sire_procedencia)) { const c = dane(d.sire_procedencia); if (c) this.selectProcDaneCountry(c); }
      if (has(d.procedencia_municipio_code)) {
        this.$set(f, "procedencia_municipio_code", d.procedencia_municipio_code);
        this.$set(f, "procedencia_municipio_nombre", d.procedencia_municipio_nombre || "");
      }
      if (has(d.sire_destino)) { const c = dane(d.sire_destino); if (c) this.selectDestDaneCountry(c); }

      // Italia — Alloggiati Web (códigos de 9 cifras)
      if (has(d.paese_nascita_code)) {
        const st = this.statiOptions.find((s) => s.code === String(d.paese_nascita_code));
        if (st) this.onItalyStatoNascita(st);
      }
      if (has(d.luogo_nascita)) {
        const c = this.comuniOptions.find((x) => x.code === String(d.luogo_nascita));
        if (c) this.onItalyComuneNascita(c);
      }
      if (has(d.luogo_rilascio_doc)) {
        // Después del tick: cambiar la cittadinanza limpia antes este campo (watcher).
        this.$nextTick(() => {
          const code9 = String(d.luogo_rilascio_doc);
          const o = this.comuniOptions.find((x) => x.code === code9) || this.statiOptions.find((x) => x.code === code9);
          if (o) this.onItalyLuogoRilascio(o);
        });
      }
    },

    closeModal() {
      this.showModal = false;
      this.showNacDropdown = false;
      this.showProcCountryDropdown = false;
      this.showProcDropdown = false;
      this.showDestDropdown = false;
      document.body.style.overflow = "";
      this.$nextTick(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.clearSignature();
        }
      });
    },

    goToSignature() {
      const f = this.form;
      const t = (k) => this.$t("reservasHc.checkinsList." + k);
      if (!f.nombre)         { toastr.error(t("vNombre")); return; }
      if (!f.apellido)       { toastr.error(t("vApellido")); return; }
      if (!f.sex)            { toastr.error(t("vSexo")); return; }
      if (!f.date_of_birth)  { toastr.error(t("vBirthDate")); return; }
      if (!f.phone)          { toastr.error(t("vPhone")); return; }
      if (!f.fecha_ingreso)  { toastr.error(t("vCheckIn")); return; }
      if (!f.fecha_salida)   { toastr.error(t("vCheckOut")); return; }
      // Nacionalidad: Colombia usa DANE (sire_nacionalidad), España/otros usan webpol (pais)
      if (this.isColombia && !f.sire_nacionalidad) {
        toastr.error(t("vNationality")); return;
      }
      if (!this.isColombia && !f.pais) {
        toastr.error(t("vNationality")); return;
      }
      if (!f.type_documento) { toastr.error(t("vDocType")); return; }
      if (!f.nu_documento)   { toastr.error(t("vDocNumber")); return; }

      // Validación de formato del documento
      const docCheck = this.validarDocumento();
      if (!docCheck.ok) { this.docError = docCheck.msg; toastr.error(docCheck.msg); return; }

      // Validación email (si se ingresó)
      if (f.email) {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
        if (!emailOk) { this.emailError = t("vEmailFormat"); toastr.error(t("vEmailFormat")); return; }
      }

      if (this.requiresDocSupport && !f.document_support) {
        toastr.error(t("vDocSupport")); return;
      }

      // Adultos: deben responder si viajan con menores (España o Colombia)
      if (!this.isMinor && (this.isSpain || this.isColombia) && !f.existChildren) {
        toastr.error(t("vMinors")); return;
      }

      // Parentesco requerido si es menor O si el adulto marcó que hay menores
      if (this.requiresRelationship && !f.relationship) {
        toastr.error(t("vRelationship")); return;
      }

      if (this.isSpain) {
        if (!f.country_residence)    { toastr.error(t("vCountryResidence")); return; }
        if (!f.city_residence)       { toastr.error(t("vCityResidence")); return; }
        if (!f.address)              { toastr.error(t("vAddress")); return; }
        if (!f.zip_code)             { toastr.error(t("vZip")); return; }
        if (!f.type_payment_checkin) { toastr.error(t("vPaymentMethod")); return; }
        if (!f.payment_date_checkin) { toastr.error(t("vPaymentDate")); return; }
      }

      if (this.isColombia) {
        if (!f.sire_procedencia) { toastr.error(t("vCountryOrigin")); return; }
        if (!f.sire_destino)     { toastr.error(t("vNextDest")); return; }
        if (this.procedenciaEsColombia && !f.procedencia_municipio_code) {
          toastr.error(t("vCityOrigin")); return;
        }
      }

      // Italia — Alloggiati Web (Polizia di Stato)
      if (this.isItaly) {
        if (!f.paese_nascita) { toastr.error(t("vStatoNascita")); return; }
        if (this.isItalianBorn && !f.luogo_nascita) {
          toastr.error(t("vComuneNascita")); return;
        }
        // Luogo di rilascio: obligatorio solo para adultos (los menores van en blanco)
        if (!this.isMinor && !f.luogo_rilascio_doc) {
          toastr.error(t("vLuogoRilascio")); return;
        }
      }

      this.modalStep = 2;
      this.$nextTick(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.clearSignature();
        }
      });
    },

    undoSignature() {
      if (this.$refs.signaturePad) this.$refs.signaturePad.undoSignature();
    },
    clearSignature() {
      if (this.$refs.signaturePad) this.$refs.signaturePad.clearSignature();
    },

    async guardarCheckin() {
      if (!this.termsAccepted) {
        toastr.error(this.$t("reservasHc.checkinsList.vTerms")); return;
      }
      if (!this.$refs.signaturePad) {
        toastr.error(this.$t("reservasHc.checkinsList.vSignaturePad")); return;
      }
      const { isEmpty, data: imgData } = this.$refs.signaturePad.saveSignature();
      if (isEmpty) {
        toastr.error(this.$t("reservasHc.checkinsList.vSignature")); return;
      }

      if (this.isSpain && this.form.country_residence === "ESP") {
        this.validatePostalCode();
      }

      this.modalSaving = true;
      const f = this.form;

      try {
        const res = await axios.post(this.$base.dominio + "/checkin/manual", {
          nombre:               f.nombre,
          apellido:             f.apellido,
          email:                f.email || null,
          whatsapp:             f.phone,
          nu_documento:         f.nu_documento ? f.nu_documento.toUpperCase() : "",
          type_documento:       f.type_documento,
          pais_code:            f.pais ? f.pais.name : "",
          code:                 f.pais ? f.pais.code : "",
          code_police:          f.pais ? f.pais.id   : null,
          date_of_birth:        f.date_of_birth,
          sex:                  f.sex,
          proceso:              3,
          tipo:                 3,
          fecha_ingreso:        f.fecha_ingreso,
          fecha_salida:         f.fecha_salida,
          plantilla_id:         this.activeProperty.id,
          reserva_id:           this.reservationId || null,
          img:                  imgData,
          idioma:               this.$i18n?.locale || "es",
          relationship:         f.relationship || null,
          document_support:     f.document_support || null,
          emision:              f.emision || null,
          // Spain
          type_payment_checkin: f.type_payment_checkin || null,
          payment_date_checkin: f.payment_date_checkin || null,
          address:              f.address || null,
          zip_code:             f.zip_code || null,
          municipio_code:       this.municipio_code || null,
          municipio_nombre:     this.municipio_nombre || null,
          country_residence:    f.country_residence || null,
          city_residence:       f.city_residence || null,
          // Colombia — SIRE/TRA
          nacionalidad_code_num:        f.sire_nacionalidad || null,
          sire_procedencia:             f.sire_procedencia || null,
          sire_destino:                 f.sire_destino || "169",
          procedencia_municipio_code:   f.procedencia_municipio_code || null,
          procedencia_municipio_nombre: f.procedencia_municipio_nombre || null,
          // Italia — Alloggiati Web (códigos de 9 cifras)
          paese_nascita_code:           this.isItaly && f.paese_nascita ? f.paese_nascita.code : null,
          luogo_nascita:                this.isItaly ? (f.luogo_nascita || null) : null,
          luogo_rilascio_doc:           this.isItaly && !this.isMinor ? (f.luogo_rilascio_doc || null) : null,
        });

        if (res.data?.ok === "true" || res.data?.ok === true) {
          const historialId = res.data?.historial?.id || null;
          const clienteId   = res.data?.historial?.cliente_id || null;

          // Upload identity photos if verification is required and photos are provided
          if (this.requiresIdentityVerification && (this.photoFront || this.photoSelfie)) {
            await this.uploadIdentityPhotos(historialId, clienteId);
          }

          // Upload minor authorization photo if provided
          if (this.isMinor && this.photoMinorAuth && historialId) {
            await this.uploadMinorAuthPhoto(historialId, clienteId);
          }

          const user = this.$store.state.userRoot;
          if (SocketioService.socket && user) {
            SocketioService.socket.emit("chat-message", {
              title: this.activeProperty.nombre,
              numero_documento: historialId,
              receptor: user.id,
              mensaje: this.$t("reservasHc.checkinsList.socketCheckinDone", {
                name: `${f.nombre} ${f.apellido}`,
              }),
              hora: new Date().getTime(),
            });
          }
          await this.fetchCheckins();
          this.modalStep = 3;
        } else {
          toastr.error(this.$t("reservasHc.checkinsList.saveCheckinError"));
        }
      } catch (e) {
        console.error("Error guardando checkin manual:", e);
        toastr.error(e?.response?.data?.message || this.$t("reservasHc.checkinsList.saveCheckinErrorGeneric"));
      } finally {
        this.modalSaving = false;
      }
    },

    async uploadIdentityPhotos(historialId, clienteId) {
      try {
        const fd = new FormData();
        if (this.photoFront)  fd.append("frontDocument", this.photoFront);
        if (this.photoSelfie) fd.append("selfie", this.photoSelfie);
        fd.append("propertyId",    this.activeProperty.id);
        fd.append("reservationId", this.reservationId || "");
        if (historialId) fd.append("checkin_id", historialId);
        if (clienteId)   fd.append("clientId",   clienteId);

        await axios.post(this.$base.dominio + "/verify-identity", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        console.warn("Error subiendo fotos de identidad:", e?.response?.data?.message || e);
        // Non-blocking: checkin already saved, just warn
        toastr.warning(this.$t("reservasHc.checkinsList.identityPhotosError"));
      }
    },

    async uploadMinorAuthPhoto(historialId, clienteId) {
      try {
        const fd = new FormData();
        fd.append("frontDocument", this.photoMinorAuth);
        fd.append("propertyId",    this.activeProperty.id);
        fd.append("reservationId", this.reservationId || "");
        if (historialId) fd.append("checkin_id", historialId);
        if (clienteId)   fd.append("clientId",   clienteId);

        await axios.post(this.$base.dominio + "/verify-identity", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        console.warn("Error subiendo autorización del menor:", e);
        toastr.warning(this.$t("reservasHc.checkinsList.minorAuthError"));
      }
    },

    // ── Handlers de validación en tiempo real ────────────────────────────────

    onDocBlur() {
      if (!this.form.nu_documento) { this.docError = ""; return; }
      const result = this.validarDocumento();
      this.docError = result.ok ? "" : result.msg;
    },

    onDocTypeChange() {
      // Al cambiar tipo de documento limpiamos el error para que revalide al salir
      this.docError = "";
    },

    onEmailBlur() {
      const email = (this.form.email || "").trim();
      if (!email) { this.emailError = ""; return; }
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      this.emailError = valid ? "" : this.$t("reservasHc.checkinsList.vEmailFormat");
    },

    // ── Validaciones de documentos ───────────────────────────────────────────

    /** DNI español: 8 dígitos + letra de control */
    _validarDNI(value) {
      const dni = String(value).toUpperCase().trim();
      if (!/^\d{8}[A-Z]$/.test(dni)) return false;
      const LETRAS = "TRWAGMYFPDXBNJZSQVHLCKET";
      const num    = parseInt(dni.slice(0, 8), 10) % 23;
      return dni[8] === LETRAS[num];
    },

    /** NIE español: X/Y/Z + 7 dígitos + letra de control */
    _validarNIE(value) {
      const nie = String(value).toUpperCase().trim();
      if (!/^[XYZ]\d{7}[A-Z]$/.test(nie)) return false;
      const LETRAS  = "TRWAGMYFPDXBNJZSQVHLCKET";
      const initial = { X: "0", Y: "1", Z: "2" }[nie[0]];
      const num     = parseInt(initial + nie.slice(1, 8), 10) % 23;
      return nie[8] === LETRAS[num];
    },

    /** Pasaporte genérico: 6-9 caracteres alfanuméricos */
    _validarPasaporte(value) {
      return /^[A-Z0-9]{6,20}$/i.test(String(value).trim());
    },

    // ── Validaciones de documentos Colombia ──────────────────────────────────

    /** CC — Cédula de Ciudadanía: 6-10 dígitos */
    _validarCC(value) {
      return /^\d{6,10}$/.test(String(value).trim());
    },

    /** CE — Cédula de Extranjería: 6-10 dígitos (puede incluir letras al inicio en nuevos formatos) */
    _validarCE(value) {
      return /^[A-Z0-9]{6,12}$/i.test(String(value).trim());
    },

    /** PPT / PEP: alfanumérico 4-15 caracteres */
    _validarPPT(value) {
      return /^[A-Z0-9]{4,15}$/i.test(String(value).trim());
    },

    /**
     * Punto de entrada: valida el documento según tipo y país.
     * Devuelve { ok: true } o { ok: false, msg: "mensaje de error" }.
     */
    // ── Italia — Alloggiati Web ───────────────────────────────────────────
    // Etiqueta del stato según el idioma del panel
    statoLabel(s) {
      const loc = (this.$i18n && this.$i18n.locale) || "es";
      if (loc.startsWith("it")) return s.nome;
      if (loc.startsWith("en")) return s.en || s.nome;
      return s.es || s.nome;
    },
    // Cittadinanza (f125) → alimenta form.pais con el ISO3. Independiente del
    // país de nacimiento: un italiano puede haber nacido fuera y viceversa.
    onItalyCittadinanza(stato) {
      this.selCittadinanza = stato || null;
      this.form.pais = stato
        ? { code: stato.iso3, name: this.statoLabel(stato), id: stato.iso3 }
        : null;
    },
    // Stato di nascita (f116) → si es Italia se pedirá el Comune di Nascita
    onItalyStatoNascita(stato) {
      this.selStatoNascita = stato || null;
      this.form.paese_nascita = stato || null;
      if (!stato || stato.code !== ALLOGGIATI_ITALIA_CODE) {
        this.form.luogo_nascita = "";
        this.selComuneNascita = null;
      }
    },
    // Comune di nascita (f105) → guarda el código Alloggiati de 9 cifras
    onItalyComuneNascita(comune) {
      this.selComuneNascita = comune || null;
      this.form.luogo_nascita = comune ? comune.code : "";
    },
    // Luogo di rilascio (f159) → comune (italiano) o stato (extranjero), 9 cifras
    onItalyLuogoRilascio(opt) {
      this.selLuogoRilascio = opt || null;
      this.form.luogo_rilascio_doc = opt ? opt.code : "";
    },

    validarDocumento() {
      const tipo = this.form.type_documento;
      const num  = (this.form.nu_documento || "").trim().toUpperCase();
      const t = (k, params) => this.$t("reservasHc.checkinsList." + k, params);

      if (!num) return { ok: false, msg: t("vDocNumber") };

      // ── España ──────────────────────────────────────────────────────────────
      if (this.isSpain) {
        if (tipo === "ID") {
          if (!this._validarDNI(num)) {
            return { ok: false, msg: t("docDni") };
          }
        } else if (tipo === "IM") {
          if (!this._validarNIE(num)) {
            return { ok: false, msg: t("docNie") };
          }
        } else if (tipo === "P") {
          if (!this._validarPasaporte(num)) {
            return { ok: false, msg: t("docPassport") };
          }
        }
        return { ok: true };
      }

      // ── Colombia ────────────────────────────────────────────────────────────
      if (this.isColombia) {
        if (tipo === "CC") {
          if (!this._validarCC(num)) {
            return { ok: false, msg: t("docCc") };
          }
        } else if (tipo === "CE") {
          if (!this._validarCE(num)) {
            return { ok: false, msg: t("docCe") };
          }
        } else if (tipo === "PPT" || tipo === "PEP") {
          if (!this._validarPPT(num)) {
            return { ok: false, msg: t("docPptPep", { tipo }) };
          }
        } else if (tipo === "P") {
          if (!this._validarPasaporte(num)) {
            return { ok: false, msg: t("docPassport") };
          }
        }
        return { ok: true };
      }

      return { ok: true };
    },

    async loadPostalData() {
      try {
        const { data } = await axios.get("/api-postal-json");
        this.postalData = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Error cargando datos postales:", e);
      }
    },

    validatePostalCode() {
      // Solo aplica para residentes en España
      if (this.form.country_residence !== "ESP") return;
      if (!this.form.zip_code || !this.postalData.length) return;

      const cp  = Number(String(this.form.zip_code).trim());
      const hit = this.postalData.find((x) => x.codigo_postal === cp);

      if (hit) {
        this.municipio_code   = hit.municipio_id;
        this.municipio_nombre = hit.municipio_nombre;
      } else {
        this.municipio_code   = null;
        this.municipio_nombre = "";
        toastr.warning(this.$t("reservasHc.checkinsList.zipNotFound"));
      }
    },

    // ── Click-outside handler para los searchable-selects SIRE ───────────────
    _onDocClickSire(e) {
      if (!this.showNacDropdown && !this.showProcCountryDropdown && !this.showDestDropdown && !this.showProcDropdown) return;
      const wrappers = [
        this.$refs.nacSsWrapper,
        this.$refs.procSsWrapper,
        this.$refs.citySsWrapper,
        this.$refs.destSsWrapper,
      ].filter(Boolean);
      const inside = wrappers.some((w) => w.contains(e.target));
      if (!inside) {
        this.showNacDropdown         = false;
        this.showProcCountryDropdown = false;
        this.showProcDropdown        = false;
        this.showDestDropdown        = false;
      }
    },

    // ── Colombia — SIRE/TRA helpers ──────────────────────────────────────────

    // ─ Nacionalidad ─
    selectNacCountry(country) {
      if (!country || !country.codigo) return;
      this.$set(this.form, "sire_nacionalidad",        country.codigo);
      this.$set(this.form, "sire_nacionalidad_nombre", country.nombre);
      this.showNacDropdown = false;
      this.sireNacSearch = "";
    },
    // ─ Helper: calcula posición fixed del panel justo debajo del wrapper ─
    _getDropdownStyle(refName) {
      const el = this.$refs[refName];
      if (!el) return {};
      const r = el.getBoundingClientRect();
      return { top: r.bottom + "px", left: r.left + "px", width: r.width + "px" };
    },

    toggleNacDropdown() {
      this.showProcCountryDropdown = false;
      this.showDestDropdown = false;
      if (this.showNacDropdown) { this.showNacDropdown = false; return; }
      this.nacPanelStyle = this._getDropdownStyle("nacSsWrapper");
      this.showNacDropdown = true;
      this.$nextTick(() => { if (this.$refs.nacSearchInput) this.$refs.nacSearchInput.focus(); });
    },

    // ─ País de procedencia ─
    selectProcDaneCountry(country) {
      if (!country || !country.codigo) return;
      this.$set(this.form, "sire_procedencia",        country.codigo);
      this.$set(this.form, "sire_procedencia_nombre", country.nombre);
      this.$set(this.form, "procedencia_municipio_code",   "");
      this.$set(this.form, "procedencia_municipio_nombre", "");
      this.cityProcSearch = "";
      this.showProcCountryDropdown = false;
      this.sireProcCountrySearch = "";
    },
    toggleProcCountryDropdown() {
      this.showNacDropdown = false;
      this.showDestDropdown = false;
      if (this.showProcCountryDropdown) { this.showProcCountryDropdown = false; return; }
      this.procPanelStyle = this._getDropdownStyle("procSsWrapper");
      this.showProcCountryDropdown = true;
      this.$nextTick(() => { if (this.$refs.procCountrySearchInput) this.$refs.procCountrySearchInput.focus(); });
    },

    // ─ País de destino ─
    selectDestDaneCountry(country) {
      if (!country || !country.codigo) return;
      this.$set(this.form, "sire_destino",        country.codigo);
      this.$set(this.form, "sire_destino_nombre", country.nombre);
      this.showDestDropdown = false;
      this.sireDestSearch = "";
    },
    toggleDestDropdown() {
      this.showNacDropdown = false;
      this.showProcCountryDropdown = false;
      if (this.showDestDropdown) { this.showDestDropdown = false; return; }
      this.destPanelStyle = this._getDropdownStyle("destSsWrapper");
      this.showDestDropdown = true;
      this.$nextTick(() => { if (this.$refs.destSearchInput) this.$refs.destSearchInput.focus(); });
    },
    // ─ Ciudad de procedencia (municipalities searchable select) ─
    selectProcCity(city) {
      this.$set(this.form, "procedencia_municipio_code",   city.codigo);
      this.$set(this.form, "procedencia_municipio_nombre", city.nombre);
      this.cityProcSearch = "";
      this.showProcDropdown = false;
    },
    toggleCityDropdown() {
      this.showNacDropdown = false;
      this.showProcCountryDropdown = false;
      this.showDestDropdown = false;
      if (this.showProcDropdown) { this.showProcDropdown = false; return; }
      this.cityPanelStyle = this._getDropdownStyle("citySsWrapper");
      this.showProcDropdown = true;
      this.$nextTick(() => { if (this.$refs.citySearchInput) this.$refs.citySearchInput.focus(); });
    },

    // ── Photo upload helpers ─────────────────────────────────────────────────
    onPhotoFrontChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.photoFront = file;
      this.photoFrontPreview = URL.createObjectURL(file);
    },

    onPhotoSelfieChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.photoSelfie = file;
      this.photoSelfiePreview = URL.createObjectURL(file);
    },

    onPhotoMinorAuthChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.photoMinorAuth = file;
      this.photoMinorAuthPreview = URL.createObjectURL(file);
    },

    removePhoto(type) {
      if (type === "front") {
        this.photoFront = null;
        this.photoFrontPreview = null;
        if (this.$refs.inputFront) this.$refs.inputFront.value = "";
      } else if (type === "selfie") {
        this.photoSelfie = null;
        this.photoSelfiePreview = null;
        if (this.$refs.inputSelfie) this.$refs.inputSelfie.value = "";
      } else if (type === "minor") {
        this.photoMinorAuth = null;
        this.photoMinorAuthPreview = null;
        if (this.$refs.inputMinor) this.$refs.inputMinor.value = "";
      }
    },
  },
};
</script>

<template>
  <div class="listado-checkins mt-5">

    <!-- Cabecera con botón -->
    <div class="checkins-toolbar">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <small class="box-title">
          {{ $t("booking.checkinsOf", { done: doneCount, total: totalGuests || 0 }) }}
        </small>
        <button type="button" class="btn-new-checkin" @click="openModal">
          <i class="fa-regular fa-plus"></i>
          <span>{{ $t("reservasHc.checkinsList.newCheckin") }}</span>
        </button>
      </div>

      <div
        v-if="(doneCount > 0 && hasPendingGuests) || manualDispatchResolvedAt"
        class="manual-dispatch-card"
        :class="{ 'manual-dispatch-card--sent': manualDispatchResolvedAt }"
      >
        <div class="manual-dispatch-copy">
          <div class="manual-dispatch-title">{{ $t("reservasHc.checkinsList.manualDispatchTitle") }}</div>
          <div class="manual-dispatch-text">
            {{ $t("reservasHc.checkinsList.manualDispatchText") }}
          </div>
        </div>

        <button
          v-if="canManualDispatch"
          type="button"
          class="btn-manual-dispatch"
          :disabled="manualDispatchLoading"
          @click="triggerManualDispatch"
        >
          <i :class="manualDispatchLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'"></i>
          <span>{{ manualDispatchLoading ? $t("reservasHc.checkinsList.sending") : $t("reservasHc.checkinsList.completeManualSend") }}</span>
        </button>

        <div v-else-if="manualDispatchResolvedAt" class="manual-dispatch-chip">
          <i class="fa-solid fa-circle-check"></i>
          <span>{{ $t("reservasHc.checkinsList.finalSendProcessed") }}</span>
        </div>
      </div>

      <!-- Envío manual de instrucciones de llegada (el operador puede adelantarlas) -->
      <div v-if="doneCount > 0" class="arrival-send-card">
        <div class="arrival-send-copy">
          <div class="arrival-send-title">
            <i class="fa-solid fa-location-arrow"></i>
            {{ $t("reservasHc.checkinsList.arrivalInstructions") }}
          </div>
          <div class="arrival-send-text">
            <template v-if="reservationData && reservationData.arrival_sent_at">
              {{ $t("reservasHc.checkinsList.arrivalAlreadySent") }}
            </template>
            <template v-else>
              {{ $t("reservasHc.checkinsList.arrivalAutoInfo") }}
            </template>
          </div>
        </div>
        <button
          type="button"
          class="btn-arrival-send"
          :disabled="arrivalSendLoading"
          @click="triggerArrivalSend"
        >
          <i :class="arrivalSendLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'"></i>
          <span>{{ arrivalSendLoading ? $t("reservasHc.checkinsList.sending") : (reservationData && reservationData.arrival_sent_at ? $t("reservasHc.checkinsList.resendNow") : $t("reservasHc.checkinsList.sendNow")) }}</span>
        </button>
      </div>
    </div>

    <!-- Estado completado / pendiente -->
    <div
      class="title-medium mt-2 mb-2 d-flex flex-row"
      style="font-weight:700;color:#333;align-items:center"
      v-if="totalGuests"
    >
      <div style="width:16px;margin-right:6px">
        <svg v-if="isCompleted" style="fill:#4caf50;margin-top:-3px"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd"
            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
            clip-rule="evenodd"/>
        </svg>
        <svg v-else style="fill:#ffc107;margin-top:-3px"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd"
            d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5ZM11.25 7.5a.75.75 0 0 1 1.5 0v4.5a.75.75 0 0 1-1.5 0V7.5Zm0 6.75a.75.75 0 0 1 1.5 0v.75a.75.75 0 0 1-1.5 0v-.75Z"
            clip-rule="evenodd"/>
        </svg>
      </div>
      <div>{{ isCompleted ? $t("booking.completed") : $t("booking.pendings") }}</div>
    </div>

    <!-- Lista -->
    <ul class="list-group mt-3" v-if="checkins && checkins.length"
      v-animate-css="'pulse'" style="background:transparent;text-align:left">
      <li
        class="list-group-item checkin-item d-flex align-items-center"
        v-for="(item, index) in checkins" :key="index"
      >
        <div style="width:36px;flex-shrink:0">
          <img
            v-if="guestFlagUrl(item.cliente)"
            :src="guestFlagUrl(item.cliente)"
            alt=""
            style="width:24px"
          />
        </div>
        <div class="nombre cursor d-flex flex-column text-truncate-col"
          :style="!$isMobile ? { width: '30%' } : { width: '45%' }"
          @click="activeModalCheckin(item)">
          <small>{{ $t("empresa.reservas.p39") }}</small>
          <strong class="text-truncate-val" :title="item.nombre">{{ item.nombre }}</strong>
        </div>
        <div v-if="!$isMobile" class="nombre cursor d-flex flex-column text-truncate-col"
          style="width:28%;margin-left:10px" @click="activeModalCheckin(item)">
          <small>{{ $t("empresa.reservas.p41") }}</small>
          <strong class="text-truncate-val" :title="item.cliente && item.cliente.apellido">
            {{ item.cliente && item.cliente.apellido }}
          </strong>
        </div>
        <div class="creado cursor d-flex flex-column" style="width:18%" @click="activeModalCheckin(item)">
          <small>{{ $t("booking.date") }}</small>
          <strong>{{ moment(item.created_at).format("DD/MM/YYYY") }}</strong>
        </div>
        <div class="checkin-actions d-flex flex-row" style="margin-left:auto;gap:6px;flex-shrink:0">
          <button type="button" class="btn-action btn-edit"
            @click.stop="openEditDrawer(item)" :title="$t('reservasHc.checkinsList.titleEditCheckin')">
            <i class="fa-regular fa-pen-to-square"></i>
          </button>
          <button type="button" class="btn-action btn-delete"
            @click.stop="eliminarCheckin(item, index)" :title="$t('reservasHc.checkinsList.titleDeleteCheckin')">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </div>
      </li>
    </ul>

    <div class="container" v-else style="text-align:center">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-6">
          <img v-animate-css="'zoomIn'" src="/img/marca/panda20.png" alt=""
            class="img-fluid" style="margin:auto;display:block;width:100px" />
          <br />{{ $t("booking.noCheckinsYet") }}
        </div>
      </div>
    </div>

    <!-- Drawer edición -->
    <CheckinEditDrawer
      :visible="showEditDrawer"
      :historial="editingCheckin"
      :property="activeProperty"
      :isVisibleData="isVisibleData"
      @close="handleCloseDrawer"
      @saved="handleSavedCheckin"
    />

    <!-- ═══════════════════════════════════════════════════════════════
         MODAL CHECK-IN MANUAL
    ═══════════════════════════════════════════════════════════════ -->
    <!-- Sin <transition>: el modal se portalea a <body> (v-portal-body); la
         transición de opacidad se quedaba atascada al mover el nodo → no se veía. -->
    <div v-if="showModal" v-portal-body class="cm-overlay">
        <div class="cm-dialog">

          <!-- Header -->
          <div class="cm-header">
            <div class="cm-header-left">
              <div class="cm-steps">
                <span class="cm-step" :class="{ active: modalStep >= 1, done: modalStep > 1 }">1</span>
                <span class="cm-step-line"></span>
                <span class="cm-step" :class="{ active: modalStep >= 2, done: modalStep > 2 }">2</span>
              </div>
              <span class="cm-title">
                <template v-if="modalStep === 1">{{ $t("reservasHc.checkinsList.stepGuestData") }}</template>
                <template v-else-if="modalStep === 2">{{ $t("reservasHc.checkinsList.stepSignature") }}</template>
                <template v-else>{{ $t("reservasHc.checkinsList.stepRegistered") }}</template>
              </span>
            </div>
            <button type="button" class="cm-close" @click="closeModal">
              <i class="fal fa-times"></i>
            </button>
          </div>

          <!-- ── Paso 1: Formulario ────────────────────────────────────── -->
          <div v-if="modalStep === 1" class="cm-body">

            <!-- Directorio de Clientes: reutilizar a quien ya hizo check-in -->
            <div class="cm-dir">
              <div v-if="dirAplicado" class="cm-dir-applied">
                <i class="fa-solid fa-circle-check"></i>
                <span>{{ $t("clientesDir.picker.selected") }}: <strong>{{ dirAplicado.nombre }}</strong></span>
              </div>
              <div v-if="faltanDatosPolicia" class="cm-dir-missing">
                <i class="fal fa-info-circle"></i>
                <span>{{ $t("clientesDir.picker.missingPolice") }}</span>
              </div>
              <label class="cm-label">{{ $t("clientesDir.picker.searchSaved") }}</label>
              <div class="cm-dir-search">
                <i class="fal fa-search"></i>
                <input
                  v-model="dirQuery"
                  type="text"
                  class="cm-input"
                  autocomplete="off"
                  :placeholder="$t('clientesDir.picker.searchPlaceholder')"
                  @input="buscarEnDirectorio"
                  @focus="dirOpen = dirResults.length > 0"
                  @blur="cerrarDirConPausa"
                />
                <div v-if="dirOpen" class="cm-dir-list">
                  <div v-if="dirLoading && !dirResults.length" class="cm-dir-empty"><i class="fa-solid fa-spinner fa-spin"></i></div>
                  <div v-else-if="!dirResults.length" class="cm-dir-empty">{{ $t("clientesDir.picker.noResults") }}</div>
                  <button
                    v-for="c in dirResults"
                    :key="c.id"
                    type="button"
                    class="cm-dir-item"
                    @mousedown.prevent="elegirCliente(c.id)"
                  >
                    <strong>{{ c.nombre_completo || $t("clientesDir.noName") }}</strong>
                    <small>{{ [c.nu_documento, c.telefono, c.email].filter(Boolean).join(" · ") }}</small>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="dirMatch" class="cm-dir-match">
              <div class="cm-dir-match__txt">
                <strong>{{ $t("clientesDir.match.title") }}</strong>
                <span>{{ $t("clientesDir.match.text", { name: dirMatch.nombre_completo || "" }) }}</span>
                <small>{{ [dirMatch.nu_documento, dirMatch.telefono].filter(Boolean).join(" · ") }}</small>
              </div>
              <div class="cm-dir-match__btns">
                <button type="button" class="cm-dir-btn cm-dir-btn--primary" @click="aplicarCliente(dirMatch)">{{ $t("clientesDir.match.use") }}</button>
                <button type="button" class="cm-dir-btn" @click="descartarCoincidencia">{{ $t("clientesDir.match.dismiss") }}</button>
              </div>
            </div>

            <div v-if="companerosPendientes.length" class="cm-dir-companions">
              <div class="cm-dir-companions__head">
                <strong>{{ $t("clientesDir.match.companionsTitle") }}</strong>
                <small>{{ $t("clientesDir.match.companionsHint") }}</small>
              </div>
              <div class="cm-dir-chips">
                <button v-for="a in companerosPendientes" :key="a.id" type="button" class="cm-dir-chip" @click="elegirCliente(a.id)">
                  <i class="fal fa-user-plus"></i> {{ a.nombre_completo || $t("clientesDir.noName") }}
                </button>
              </div>
            </div>

            <!-- Datos personales -->
            <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionPersonal") }}</div>
            <div class="cm-row">
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.nombre") }} *</label>
                <input v-model="form.nombre" type="text" class="cm-input" />
              </div>
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.apellido") }} *</label>
                <input v-model="form.apellido" type="text" class="cm-input" />
              </div>
            </div>

            <div class="cm-row">
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.sexo") }} *</label>
                <select v-model="form.sex" class="cm-select">
                  <option value="">{{ $t("reservasHc.checkinsList.select") }}</option>
                  <option value="M">{{ $t("reservasHc.checkinsList.male") }}</option>
                  <option value="F">{{ $t("reservasHc.checkinsList.female") }}</option>
                  <option value="Prefiero no decirlo">{{ $t("reservasHc.checkinsList.preferNotToSay") }}</option>
                  <option value="Otro">{{ $t("reservasHc.checkinsList.other") }}</option>
                </select>
              </div>
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.birthDate") }} *</label>
                <input v-model="form.date_of_birth" type="date" class="cm-input" />
              </div>
            </div>

            <!-- Adulto — ¿viajan menores? (España y Colombia) -->
            <div class="cm-row" v-if="!isMinor && (isSpain || isColombia) && form.date_of_birth">
              <div class="cm-field" style="flex:1 1 100%">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.minorsTraveling") }} *</label>
                <select v-model="form.existChildren" class="cm-select">
                  <option value="">{{ $t("reservasHc.checkinsList.select") }}</option>
                  <option value="no">{{ $t("reservasHc.checkinsList.no") }}</option>
                  <option value="si">{{ $t("reservasHc.checkinsList.yes") }}</option>
                </select>
              </div>
            </div>

            <!-- Parentesco — aparece si es menor O si el adulto declaró que hay menores -->
            <div class="cm-row" v-if="requiresRelationship && (isSpain || isColombia)">
              <div class="cm-field" style="flex:1 1 100%">
                <label class="cm-label">
                  {{ isMinor ? $t("reservasHc.checkinsList.relationshipMinor") : $t("reservasHc.checkinsList.relationshipToMinor") }} *
                </label>
                <select v-model="form.relationship" class="cm-select">
                  <option :value="null" disabled>{{ $t("reservasHc.checkinsList.select") }}</option>
                  <option
                    v-for="(rel, ridx) in relationshipOptions"
                    :key="'rel-' + ridx"
                    :value="rel.value"
                  >{{ rel.label }}</option>
                </select>
              </div>
            </div>

            <div class="cm-row">
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.phone") }} *</label>
                <vue-tel-input
                  v-model="form.phone"
                  :key="defaultCountryCode"
                  :autoDefaultCountry="true"
                  :defaultCountry="defaultCountryCode"
                  :inputOptions="{ showDialCode: true, placeholder: '' }"
                  class="cm-tel-input"
                />
              </div>
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.email") }}</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="cm-input"
                  :class="{ 'cm-input-error': emailError }"
                  @blur="onEmailBlur"
                />
                <span v-if="emailError" class="cm-field-error">{{ emailError }}</span>
              </div>
            </div>

            <!-- Estancia -->
            <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionStay") }}</div>
            <div class="cm-row">
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.checkInDate") }} *</label>
                <input v-model="form.fecha_ingreso" type="date" class="cm-input" />
              </div>
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.checkOutDate") }} *</label>
                <input v-model="form.fecha_salida" type="date" class="cm-input" :min="form.fecha_ingreso" />
              </div>
            </div>

            <!-- Documento -->
            <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionDocument") }}</div>
            <div class="cm-row">
              <!-- Nacionalidad webpol — España y otros (Colombia usa DANE, Italia usa Alloggiati) -->
              <div class="cm-field" v-if="!isColombia && !isItaly">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.nationality") }} *</label>
                <select v-model="form.pais" class="cm-select">
                  <option :value="null" disabled>{{ $t("reservasHc.checkinsList.select") }}</option>
                  <option v-for="(p, pidx) in paisesNacionalidad" :key="'pn-' + pidx" :value="p">{{ p.name }}</option>
                </select>
              </div>
              <!-- Italia: Cittadinanza (f125) con códigos Alloggiati Web — INDEPENDIENTE del país de nacimiento -->
              <div class="cm-field" v-if="isItaly">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.cittadinanza") }} *</label>
                <multiselect
                  v-model="selCittadinanza"
                  :options="statiOptions"
                  label="label"
                  track-by="code"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="true"
                  :placeholder="$t('reservasHc.checkinsList.searchCittadinanza')"
                  @input="onItalyCittadinanza"
                />
              </div>
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.documentType") }} *</label>
                <select v-model="form.type_documento" class="cm-select" @change="onDocTypeChange">
                  <option value="">{{ $t("reservasHc.checkinsList.select") }}</option>
                  <option v-for="(dt, dtidx) in docTypes" :key="'dt-' + dtidx" :value="dt.value">{{ dt.label }}</option>
                </select>
              </div>
            </div>

            <div class="cm-row">
              <div class="cm-field">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.documentNumber") }} *</label>
                <input
                  v-model="form.nu_documento"
                  type="text"
                  class="cm-input"
                  :class="{ 'cm-input-error': docError }"
                  style="text-transform:uppercase"
                  @blur="onDocBlur"
                  @input="docError = ''"
                />
                <span v-if="docError" class="cm-field-error">{{ docError }}</span>
              </div>
              <div class="cm-field" v-if="requiresDocSupport">
                <label class="cm-label">{{ $t("reservasHc.checkinsList.supportNumber") }} *</label>
                <input v-model="form.document_support" type="text" class="cm-input"
                  style="text-transform:uppercase" />
              </div>
            </div>

            <!-- ── Italia: campos Alloggiati Web (Polizia di Stato) ─── -->
            <template v-if="isItaly">
              <div class="cm-italy-banner">
                <span class="cm-italy-flag">🇮🇹</span>
                <div class="cm-italy-banner-txt">
                  <strong>{{ $t("reservasHc.checkinsList.italyBannerTitle") }}</strong>
                  <span>{{ $t("reservasHc.checkinsList.italyBannerText") }}</span>
                </div>
              </div>

              <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionBirthData") }}</div>

              <!-- Stato di nascita (f116) — separado de la cittadinanza -->
              <div class="cm-row">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.statoNascita") }} *</label>
                  <multiselect
                    v-model="selStatoNascita"
                    :options="statiOptions"
                    label="label"
                    track-by="code"
                    :searchable="true"
                    :show-labels="false"
                    :allow-empty="true"
                    :placeholder="$t('reservasHc.checkinsList.searchStatoNascita')"
                    @input="onItalyStatoNascita"
                  />
                  <small class="cm-hint">{{ $t("reservasHc.checkinsList.birthStateHint") }}</small>
                </div>
              </div>

              <!-- Comune di nascita — solo si nació en Italia (código Alloggiati 9 cifras) -->
              <div class="cm-row" v-if="isItalianBorn">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.comuneNascita") }} *</label>
                  <multiselect
                    v-model="selComuneNascita"
                    :options="comuniOptions"
                    label="label"
                    track-by="code"
                    :searchable="true"
                    :show-labels="false"
                    :allow-empty="true"
                    :options-limit="50"
                    :placeholder="$t('reservasHc.checkinsList.searchComuneNascita')"
                    @input="onItalyComuneNascita"
                  />
                </div>
              </div>

              <!-- Luogo di rilascio del documento — solo adultos (menores van en blanco) -->
              <div class="cm-row" v-if="!isMinor">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.luogoRilascio") }} *</label>
                  <!-- Ciudadano italiano → documento expedido en un comune italiano -->
                  <multiselect
                    v-if="isItalianCitizen"
                    v-model="selLuogoRilascio"
                    :options="comuniOptions"
                    label="label"
                    track-by="code"
                    :searchable="true"
                    :show-labels="false"
                    :allow-empty="true"
                    :options-limit="50"
                    :placeholder="$t('reservasHc.checkinsList.searchComuneRilascio')"
                    @input="onItalyLuogoRilascio"
                  />
                  <!-- Ciudadano extranjero → documento expedido en su país (código de estado) -->
                  <multiselect
                    v-else
                    v-model="selLuogoRilascio"
                    :options="statiOptions"
                    label="label"
                    track-by="code"
                    :searchable="true"
                    :show-labels="false"
                    :allow-empty="true"
                    :placeholder="$t('reservasHc.checkinsList.searchStatoRilascio')"
                    @input="onItalyLuogoRilascio"
                  />
                  <small class="cm-hint">{{ $t("reservasHc.checkinsList.luogoRilascioHint") }}</small>
                </div>
              </div>
            </template>

            <!-- ── España: campos SES Hospedajes ───────────────────── -->
            <template v-if="isSpain">
              <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionSes") }}</div>

              <div class="cm-row">
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.countryResidence") }} *</label>
                  <select v-model="form.country_residence" class="cm-select">
                    <option value="">{{ $t("reservasHc.checkinsList.select") }}</option>
                    <option v-for="(pr, prid) in paisesResidencia" :key="'pr-' + prid" :value="pr.code">
                      {{ pr.nombre }}
                    </option>
                  </select>
                </div>
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.cityResidence") }} *</label>
                  <input v-model="form.city_residence" type="text" class="cm-input" />
                </div>
              </div>

              <div class="cm-row">
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.address") }} *</label>
                  <input v-model="form.address" type="text" class="cm-input" />
                </div>
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.zipCode") }} *</label>
                  <input v-model="form.zip_code" type="text" class="cm-input"
                    @blur="validatePostalCode" />
                  <small v-if="municipio_nombre" class="cm-hint">
                    <i class="fas fa-map-marker-alt"></i> {{ municipio_nombre }}
                  </small>
                </div>
              </div>

              <div class="cm-row">
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.paymentMethod") }} *</label>
                  <select v-model="form.type_payment_checkin" class="cm-select">
                    <option value="">{{ $t("reservasHc.checkinsList.select") }}</option>
                    <option value="EFECT">{{ $t("reservasHc.checkinsList.pay.efectivo") }}</option>
                    <option value="DESTI">{{ $t("reservasHc.checkinsList.pay.destino") }}</option>
                    <option value="TARJT">{{ $t("reservasHc.checkinsList.pay.tarjeta") }}</option>
                    <option value="MOVIL">{{ $t("reservasHc.checkinsList.pay.movil") }}</option>
                    <option value="PLATF">{{ $t("reservasHc.checkinsList.pay.plataforma") }}</option>
                    <option value="TREG">{{ $t("reservasHc.checkinsList.pay.transferencia") }}</option>
                    <option value="OTRO">{{ $t("reservasHc.checkinsList.other") }}</option>
                  </select>
                </div>
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.paymentDate") }} *</label>
                  <input v-model="form.payment_date_checkin" type="date" class="cm-input" />
                </div>
              </div>
            </template>

            <!-- ── Colombia: campos SIRE/TRA ──────────────────────── -->
            <template v-if="isColombia">

              <!-- Aviso legal SIRE & TRA -->
              <div class="cm-sire-notice">
                <strong>{{ $t("reservasHc.checkinsList.sireLegalTitle") }}</strong>
                <p>{{ $t("reservasHc.checkinsList.sireLegalText") }}</p>
              </div>

              <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionSire") }}</div>

              <!-- ─── Nacionalidad (DANE) ─── -->
              <div class="cm-row">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.nationality") }} *</label>
                  <div ref="nacSsWrapper" class="cm-ss" :class="{ 'cm-ss-open': showNacDropdown }">
                    <div class="cm-ss-trigger" @click="toggleNacDropdown">
                      <span :class="['cm-ss-value', { 'cm-ss-placeholder': !form.sire_nacionalidad_nombre }]">
                        {{ form.sire_nacionalidad_nombre || $t("reservasHc.checkinsList.searchNationality") }}
                      </span>
                      <i :class="['fas', showNacDropdown ? 'fa-chevron-up' : 'fa-chevron-down', 'cm-ss-arrow']"></i>
                    </div>
                    <div v-if="showNacDropdown" class="cm-ss-panel" :style="nacPanelStyle">
                      <div class="cm-ss-search-box">
                        <i class="fas fa-search"></i>
                        <input ref="nacSearchInput" v-model="sireNacSearch" type="text"
                               class="cm-ss-search-input" :placeholder="$t('reservasHc.checkinsList.searchCountry')" autocomplete="off" />
                      </div>
                      <div class="cm-ss-list">
                        <div v-if="!filteredNacCountries.length" class="cm-ss-empty">{{ $t("reservasHc.checkinsList.noResults") }}</div>
                        <div v-for="(c, cidx) in filteredNacCountries" :key="'nac-' + cidx"
                             class="cm-ss-item"
                             :class="{ 'cm-ss-item-selected': c.codigo === form.sire_nacionalidad }"
                             @mousedown.prevent="selectNacCountry(c)">
                          {{ c.nombre }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ─── País de procedencia ─── -->
              <div class="cm-row">
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.countryOrigin") }} *</label>
                  <div ref="procSsWrapper" class="cm-ss" :class="{ 'cm-ss-open': showProcCountryDropdown }">
                    <div class="cm-ss-trigger" @click="toggleProcCountryDropdown">
                      <span :class="['cm-ss-value', { 'cm-ss-placeholder': !form.sire_procedencia_nombre }]">
                        {{ form.sire_procedencia_nombre || $t("reservasHc.checkinsList.searchCountryOrigin") }}
                      </span>
                      <i :class="['fas', showProcCountryDropdown ? 'fa-chevron-up' : 'fa-chevron-down', 'cm-ss-arrow']"></i>
                    </div>
                    <div v-if="showProcCountryDropdown" class="cm-ss-panel" :style="procPanelStyle">
                      <div class="cm-ss-search-box">
                        <i class="fas fa-search"></i>
                        <input ref="procCountrySearchInput" v-model="sireProcCountrySearch" type="text"
                               class="cm-ss-search-input" :placeholder="$t('reservasHc.checkinsList.searchCountry')" autocomplete="off" />
                      </div>
                      <div class="cm-ss-list">
                        <div v-if="!filteredProcCountries.length" class="cm-ss-empty">{{ $t("reservasHc.checkinsList.noResults") }}</div>
                        <div v-for="(c, cidx) in filteredProcCountries" :key="'proc-' + cidx"
                             class="cm-ss-item"
                             :class="{ 'cm-ss-item-selected': c.codigo === form.sire_procedencia }"
                             @mousedown.prevent="selectProcDaneCountry(c)">
                          {{ c.nombre }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ciudad de procedencia — solo si procedencia = Colombia (169) -->
                <div class="cm-field" v-if="procedenciaEsColombia">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.cityOrigin") }} *</label>
                  <div ref="citySsWrapper" class="cm-ss" :class="{ 'cm-ss-open': showProcDropdown }">
                    <div class="cm-ss-trigger" @click="toggleCityDropdown">
                      <span :class="['cm-ss-value', { 'cm-ss-placeholder': !form.procedencia_municipio_nombre }]">
                        {{ form.procedencia_municipio_nombre || $t("reservasHc.checkinsList.searchCity") }}
                        <template v-if="form.procedencia_municipio_code">
                          <span class="cm-ss-code">({{ form.procedencia_municipio_code }})</span>
                        </template>
                      </span>
                      <i :class="['fas', showProcDropdown ? 'fa-chevron-up' : 'fa-chevron-down', 'cm-ss-arrow']"></i>
                    </div>
                    <div v-if="showProcDropdown" class="cm-ss-panel" :style="cityPanelStyle">
                      <div class="cm-ss-search-box">
                        <i class="fas fa-search"></i>
                        <input ref="citySearchInput" v-model="cityProcSearch" type="text"
                               class="cm-ss-search-input" :placeholder="$t('reservasHc.checkinsList.searchMunicipality')" autocomplete="off" />
                      </div>
                      <div class="cm-ss-list">
                        <div v-if="!filteredProcCities.length" class="cm-ss-empty">{{ $t("reservasHc.checkinsList.noResults") }}</div>
                        <div v-for="(city, cidx) in filteredProcCities" :key="'cproc-' + cidx"
                             class="cm-ss-item"
                             :class="{ 'cm-ss-item-selected': city.codigo === form.procedencia_municipio_code }"
                             @mousedown.prevent="selectProcCity(city)">
                          <span>{{ city.nombre }}</span>
                          <span class="cm-ss-code-badge">{{ city.codigo }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ─── País próximo destino ─── -->
              <div class="cm-row">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.nextDestinationCountry") }} *</label>
                  <div ref="destSsWrapper" class="cm-ss" :class="{ 'cm-ss-open': showDestDropdown }">
                    <div class="cm-ss-trigger" @click="toggleDestDropdown">
                      <span :class="['cm-ss-value', { 'cm-ss-placeholder': !form.sire_destino_nombre }]">
                        {{ form.sire_destino_nombre || $t("reservasHc.checkinsList.searchDestinationCountry") }}
                      </span>
                      <i :class="['fas', showDestDropdown ? 'fa-chevron-up' : 'fa-chevron-down', 'cm-ss-arrow']"></i>
                    </div>
                    <div v-if="showDestDropdown" class="cm-ss-panel" :style="destPanelStyle">
                      <div class="cm-ss-search-box">
                        <i class="fas fa-search"></i>
                        <input ref="destSearchInput" v-model="sireDestSearch" type="text"
                               class="cm-ss-search-input" :placeholder="$t('reservasHc.checkinsList.searchCountry')" autocomplete="off" />
                      </div>
                      <div class="cm-ss-list">
                        <div v-if="!filteredDestCountries.length" class="cm-ss-empty">{{ $t("reservasHc.checkinsList.noResults") }}</div>
                        <div v-for="(c, cidx) in filteredDestCountries" :key="'dest-' + cidx"
                             class="cm-ss-item"
                             :class="{ 'cm-ss-item-selected': c.codigo === form.sire_destino }"
                             @mousedown.prevent="selectDestDaneCountry(c)">
                          {{ c.nombre }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ─── Tarjeta resumen SIRE ─── -->
              <div class="cm-sire-summary" v-if="form.sire_procedencia">
                <div class="cm-sire-row" v-if="form.sire_nacionalidad_nombre">
                  <span class="cm-sire-label">{{ $t("reservasHc.checkinsList.nationality") }}:</span>
                  <span class="cm-sire-val">{{ form.sire_nacionalidad_nombre }}</span>
                </div>
                <div class="cm-sire-row" v-if="form.sire_procedencia_nombre">
                  <span class="cm-sire-label">{{ $t("reservasHc.checkinsList.summaryOrigin") }}:</span>
                  <span class="cm-sire-val">
                    <template v-if="form.procedencia_municipio_nombre">
                      {{ form.procedencia_municipio_nombre }} ({{ form.sire_procedencia_nombre }})
                    </template>
                    <template v-else>{{ form.sire_procedencia_nombre }}</template>
                  </span>
                </div>
                <div class="cm-sire-row" v-if="form.sire_destino_nombre">
                  <span class="cm-sire-label">{{ $t("reservasHc.checkinsList.summaryDestination") }}:</span>
                  <span class="cm-sire-val">{{ form.sire_destino_nombre }}</span>
                </div>
              </div>

            </template>

            <!-- ── Verificación de identidad (Colombia + r_selfie) ─── -->
            <template v-if="requiresIdentityVerification">
              <div class="cm-section">
                {{ $t("reservasHc.checkinsList.sectionIdentity") }}
                <span class="cm-badge-optional">{{ $t("reservasHc.checkinsList.recommended") }}</span>
              </div>
              <p class="cm-info-text">
                <i class="fas fa-shield-alt" style="color:#16a34a;margin-right:5px"></i>
                {{ $t("reservasHc.checkinsList.identityActiveInfo") }}
              </p>

              <div class="cm-row">
                <!-- Foto frontal del documento -->
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.documentFront") }}</label>
                  <div class="cm-photo-upload" :class="{ 'has-photo': photoFrontPreview }">
                    <template v-if="!photoFrontPreview">
                      <label class="cm-photo-label" :for="'photo-front-' + reservationId">
                        <i class="fal fa-id-card"></i>
                        <span>{{ $t("reservasHc.checkinsList.uploadPhoto") }}</span>
                      </label>
                      <input
                        :id="'photo-front-' + reservationId"
                        ref="inputFront"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg"
                        style="display:none"
                        @change="onPhotoFrontChange"
                      />
                    </template>
                    <template v-else>
                      <img :src="photoFrontPreview" class="cm-photo-preview" :alt="$t('reservasHc.checkinsList.altDocumentFront')" />
                      <button type="button" class="cm-photo-remove" @click="removePhoto('front')" :title="$t('reservasHc.checkinsList.titleRemove')">
                        <i class="fal fa-times"></i>
                      </button>
                    </template>
                  </div>
                </div>

                <!-- Selfie -->
                <div class="cm-field">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.selfieGuest") }}</label>
                  <div class="cm-photo-upload" :class="{ 'has-photo': photoSelfiePreview }">
                    <template v-if="!photoSelfiePreview">
                      <label class="cm-photo-label" :for="'photo-selfie-' + reservationId">
                        <i class="fal fa-camera"></i>
                        <span>{{ $t("reservasHc.checkinsList.uploadPhoto") }}</span>
                      </label>
                      <input
                        :id="'photo-selfie-' + reservationId"
                        ref="inputSelfie"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg"
                        style="display:none"
                        @change="onPhotoSelfieChange"
                      />
                    </template>
                    <template v-else>
                      <img :src="photoSelfiePreview" class="cm-photo-preview" alt="Selfie" />
                      <button type="button" class="cm-photo-remove" @click="removePhoto('selfie')" :title="$t('reservasHc.checkinsList.titleRemove')">
                        <i class="fal fa-times"></i>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </template>

            <!-- ── Autorización de menor ─────────────────────────── -->
            <template v-if="isMinor">
              <div class="cm-section">
                {{ $t("reservasHc.checkinsList.sectionMinorDocs") }}
                <span class="cm-badge-optional">{{ $t("reservasHc.checkinsList.optional") }}</span>
              </div>
              <p class="cm-info-text">
                <i class="fas fa-child" style="color:#f59e0b;margin-right:5px"></i>
                {{ $t("reservasHc.checkinsList.minorInfo") }}
              </p>
              <div class="cm-row">
                <div class="cm-field" style="flex:1 1 100%">
                  <label class="cm-label">{{ $t("reservasHc.checkinsList.guardianAuthDoc") }}</label>
                  <div class="cm-photo-upload" :class="{ 'has-photo': photoMinorAuthPreview }">
                    <template v-if="!photoMinorAuthPreview">
                      <label class="cm-photo-label" :for="'photo-minor-' + reservationId">
                        <i class="fal fa-file-image"></i>
                        <span>{{ $t("reservasHc.checkinsList.uploadDocument") }}</span>
                      </label>
                      <input
                        :id="'photo-minor-' + reservationId"
                        ref="inputMinor"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        style="display:none"
                        @change="onPhotoMinorAuthChange"
                      />
                    </template>
                    <template v-else>
                      <img :src="photoMinorAuthPreview" class="cm-photo-preview" :alt="$t('reservasHc.checkinsList.altMinorAuth')" />
                      <button type="button" class="cm-photo-remove" @click="removePhoto('minor')" :title="$t('reservasHc.checkinsList.titleRemove')">
                        <i class="fal fa-times"></i>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </template>

          </div>

          <!-- ── Paso 2: Firma ──────────────────────────────────────────── -->
          <div v-else-if="modalStep === 2" class="cm-body">
            <div class="cm-section">{{ $t("reservasHc.checkinsList.sectionSignature") }}</div>
            <p style="font-size:13px;color:#6b7280;margin-bottom:14px">
              {{ $t("reservasHc.checkinsList.signatureHint") }}
            </p>

            <div class="cm-sig-box">
              <VueSignaturePad
                ref="signaturePad"
                style="width:100%;height:200px;background:#fafafa;border-radius:10px;border:2px dashed #d1d5db;"
              />
            </div>

            <div class="cm-sig-actions">
              <button type="button" class="cm-btn-ghost" @click="undoSignature">
                <i class="fas fa-undo" style="margin-right:5px"></i>{{ $t("reservasHc.checkinsList.undo") }}
              </button>
              <button type="button" class="cm-btn-ghost" @click="clearSignature">
                <i class="fas fa-trash" style="margin-right:5px"></i>{{ $t("reservasHc.checkinsList.clear") }}
              </button>
            </div>

            <div class="cm-terms-box">
              <label class="cm-chk-label">
                <input type="checkbox" v-model="termsAccepted" />
                <span>{{ $t("reservasHc.checkinsList.termsAccept") }}</span>
              </label>
            </div>
          </div>

          <!-- ── Paso 3: Éxito ──────────────────────────────────────────── -->
          <div v-else class="cm-body cm-success">
            <div class="cm-success-icon"><i class="fal fa-check-circle"></i></div>
            <h3 class="cm-success-title">{{ $t("reservasHc.checkinsList.successTitle") }}</h3>
            <p class="cm-success-sub">
              {{ $t("reservasHc.checkinsList.successBefore") }} <strong>{{ form.nombre }} {{ form.apellido }}</strong>
              {{ $t("reservasHc.checkinsList.successAfter") }}
            </p>
            <!-- Acompañantes de antes: registrarlos de un toque, ya rellenos -->
            <div v-if="companerosPendientes.length" class="cm-dir-companions cm-dir-companions--success">
              <div class="cm-dir-companions__head">
                <strong>{{ $t("clientesDir.match.companionsTitle") }}</strong>
              </div>
              <div class="cm-dir-chips">
                <button v-for="a in companerosPendientes" :key="a.id" type="button" class="cm-dir-chip cm-dir-chip--strong" @click="openModalConCliente(a.id)">
                  <i class="fal fa-user-plus"></i> {{ $t("clientesDir.match.registerCompanion", { name: a.nombre_completo || "" }) }}
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="cm-footer">
            <template v-if="modalStep === 1">
              <button type="button" class="cm-btn-cancel" @click="closeModal">{{ $t("reservasHc.checkinsList.cancel") }}</button>
              <button type="button" class="cm-btn-primary" @click="goToSignature">
                {{ $t("reservasHc.checkinsList.next") }} <i class="fal fa-chevron-right ml-1"></i>
              </button>
            </template>
            <template v-else-if="modalStep === 2">
              <button type="button" class="cm-btn-cancel" @click="modalStep = 1" :disabled="modalSaving">
                <i class="fal fa-chevron-left mr-1"></i>{{ $t("reservasHc.checkinsList.back") }}
              </button>
              <button type="button" class="cm-btn-primary" @click="guardarCheckin" :disabled="modalSaving">
                <span v-if="modalSaving"><i class="fas fa-spinner fa-spin mr-1"></i>{{ $t("reservasHc.checkinsList.saving") }}</span>
                <span v-else><i class="fal fa-check mr-1"></i>{{ $t("reservasHc.checkinsList.saveCheckin") }}</span>
              </button>
            </template>
            <template v-else>
              <button type="button" class="cm-btn-primary" @click="closeModal">{{ $t("reservasHc.checkinsList.close") }}</button>
            </template>
          </div>

        </div>
      </div>
  </div>
</template>

<style lang="scss" scoped>
// Lista
.listado-checkins .box-title {
  font-size: 12px; color: #6b7280; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.checkins-toolbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.manual-dispatch-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff9eb;
  border: 1px solid #fde68a;
}
.manual-dispatch-card--sent {
  background: #eefbf3;
  border-color: #bbf7d0;
}
.manual-dispatch-copy {
  min-width: 0;
}
.manual-dispatch-title {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}
.manual-dispatch-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
}
.btn-manual-dispatch {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border: none;
  border-radius: 12px;
  background: #673de6;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(103, 61, 230, 0.18);
  transition: all 0.18s ease;
  cursor: pointer;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(103, 61, 230, 0.24);
  }
  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }
}
.manual-dispatch-chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 12px;
  font-weight: 700;
}
.arrival-send-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f5f2ff;
  border: 1px solid #e5dcff;
}
.arrival-send-copy { min-width: 0; }
.arrival-send-title {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  i { color: #673de6; }
}
.arrival-send-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
}
.btn-arrival-send {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border: none;
  border-radius: 12px;
  background: #673de6;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(103, 61, 230, 0.18);
  transition: all 0.18s ease;
  cursor: pointer;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(103, 61, 230, 0.24);
  }
  &:disabled { opacity: 0.72; cursor: wait; }
}
.list-group-item { border: none; margin-bottom: 10px; box-shadow: 0 0 30px 2px #eee; }
.checkin-actions { align-items: center; }
.btn-action {
  width: 32px; height: 32px; border-radius: 10px; border: none;
  background: #f3f4f6; display: inline-flex; align-items: center;
  justify-content: center; cursor: pointer; font-size: 13px; transition: all 0.18s;
  &:hover { background: #e5e7eb; transform: translateY(-1px); }
  &:active { transform: translateY(0); background: #d1d5db; }
}
.btn-edit   { color: #6b7280; &:hover { color: #2563eb; } }
.btn-delete { color: #6b7280; &:hover { color: #dc2626; } }
.text-truncate-col { min-width: 0; }
.text-truncate-val { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; max-width: 100%; }

// Botón nuevo check-in
.btn-new-checkin {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 10px; border: none;
  background: #16a34a; color: #fff; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.18s;
  box-shadow: 0 2px 8px rgba(22,163,74,0.25);
  &:hover { background: #15803d; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(22,163,74,0.3); }
  &:active { transform: translateY(0); }
}

// Overlay
.cm-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,0.55);
  backdrop-filter: blur(3px); z-index: 99999;
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.cm-dialog {
  background: #fff; border-radius: 18px; width: 100%; max-width: 580px;
  max-height: 92vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(15,23,42,0.18); overflow: hidden;
}

// Header
.cm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 16px; border-bottom: 1px solid #f1f5f9; flex-shrink: 0;
}
.cm-header-left { display: flex; align-items: center; gap: 12px; }
.cm-title { font-size: 16px; font-weight: 700; color: #1e293b; }
.cm-close {
  width: 34px; height: 34px; border-radius: 10px; border: none;
  background: #f1f5f9; color: #64748b; display: flex; align-items: center;
  justify-content: center; cursor: pointer; font-size: 15px; transition: all 0.15s;
  &:hover { background: #e2e8f0; color: #1e293b; }
}
.cm-steps { display: flex; align-items: center; gap: 4px; }
.cm-step {
  width: 24px; height: 24px; border-radius: 50%; background: #e2e8f0; color: #94a3b8;
  font-size: 11px; font-weight: 700; display: flex; align-items: center;
  justify-content: center; transition: all 0.2s;
  &.active { background: #16a34a; color: #fff; }
  &.done   { background: #bbf7d0; color: #15803d; }
}
.cm-step-line { width: 20px; height: 2px; background: #e2e8f0; border-radius: 1px; }

// Body
.cm-body { flex: 1; overflow-y: auto; padding: 20px 22px; }
.cm-section {
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 12px; margin-top: 16px;
  display: flex; align-items: center; gap: 8px;
  &:first-child { margin-top: 0; }
}
.cm-badge-optional {
  font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
  padding: 2px 6px; border-radius: 20px;
}
.cm-info-text {
  font-size: 12px; color: #64748b; margin: -6px 0 12px;
  background: #f8fafc; border-radius: 8px; padding: 8px 10px;
  border: 1px solid #e2e8f0;
}
.cm-hint {
  font-size: 11px; color: #16a34a; margin-top: 3px;
  display: block; font-weight: 500;
}
.cm-row { display: flex; gap: 14px; margin-bottom: 14px; }
.cm-field { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.cm-label { font-size: 12px; font-weight: 600; color: #374151; }
.cm-input, .cm-select {
  height: 40px; border-radius: 9px; border: 1.5px solid #e2e8f0;
  padding: 0 12px; font-size: 13px; color: #1e293b; background: #f8fafc;
  width: 100%; transition: border-color 0.15s; outline: none;
  &:focus { border-color: #16a34a; background: #fff; }
}
.cm-select { cursor: pointer; }
.cm-input-error { border-color: #ef4444 !important; background: #fff5f5 !important; }
.cm-field-error {
  font-size: 11px; color: #ef4444; font-weight: 500;
  margin-top: 3px; display: block;
}

// VueTelInput inside the modal
.cm-tel-input {
  height: 40px !important; border-radius: 9px !important;
  border: 1.5px solid #e2e8f0 !important; background: #f8fafc !important;
  font-size: 13px !important; width: 100% !important;
  &:focus-within {
    border-color: #16a34a !important;
    background: #fff !important;
    box-shadow: none !important;
  }
  :deep(.vti__dropdown) {
    border-radius: 9px 0 0 9px !important;
    background: #f8fafc !important;
    border-right: 1px solid #e2e8f0 !important;
  }
  :deep(.vti__input) {
    background: transparent !important;
    font-size: 13px !important;
    color: #1e293b !important;
    height: 100% !important;
  }
}

// ─── Searchable select (SIRE — Colombia) ───────────────────────────────────
.cm-ss {
  position: relative;
  width: 100%;
}
.cm-ss-trigger {
  display: flex; align-items: center; justify-content: space-between;
  height: 40px; padding: 0 12px; border-radius: 9px;
  border: 1.5px solid #e2e8f0; background: #fff; cursor: pointer;
  user-select: none; transition: border-color 0.15s, box-shadow 0.15s;
  .cm-ss-open & {
    border-color: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.10);
  }
}
.cm-ss-value {
  flex: 1; font-size: 13px; color: #1e293b;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cm-ss-placeholder { color: #94a3b8; }
.cm-ss-arrow { font-size: 11px; color: #94a3b8; flex-shrink: 0; margin-left: 8px; }
.cm-ss-panel {
  // position:fixed permite escapar overflow:auto del cm-body sin recortarse.
  // top/left/width los inyecta :style calculado con getBoundingClientRect().
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid #16a34a;
  border-radius: 9px;
  margin-top: 3px;         // pequeño gap visual entre trigger y panel
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}
.cm-ss-search-box {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  i { font-size: 12px; color: #94a3b8; flex-shrink: 0; }
}
.cm-ss-search-input {
  flex: 1; border: none; background: transparent;
  outline: none; font-size: 13px; color: #1e293b;
  &::placeholder { color: #94a3b8; }
}
.cm-ss-list { max-height: 210px; overflow-y: auto; }
.cm-ss-item {
  padding: 9px 13px; font-size: 13px; color: #1e293b;
  cursor: pointer; transition: background 0.1s;
  &:not(:last-child) { border-bottom: 1px solid #f8fafc; }
  &:hover { background: #f0fdf4; color: #16a34a; }
  &.cm-ss-item-selected {
    background: #dcfce7; color: #15803d; font-weight: 600;
  }
}
.cm-ss-empty {
  padding: 14px 12px; text-align: center;
  font-size: 13px; color: #94a3b8;
}
// Código de municipio en el trigger y en los items del listado
.cm-ss-code { font-size: 11px; color: #94a3b8; margin-left: 5px; }
.cm-ss-code-badge {
  font-size: 11px; color: #94a3b8; font-weight: 600;
  background: #f1f5f9; border-radius: 4px; padding: 1px 5px; flex-shrink: 0;
}
// Items de ciudad: flex para nombre + código alineados
.cm-ss-item { display: flex; align-items: center; justify-content: space-between; }

// Autocomplete ciudad (Colombia)
.cm-autocomplete {
  position: relative;
}
.cm-dropdown {
  position: absolute; top: 100%; left: 0; right: 0; z-index: 200;
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 9px;
  box-shadow: 0 8px 24px rgba(15,23,42,0.12); max-height: 220px;
  overflow-y: auto; margin-top: 3px;
}
.cm-dropdown-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; cursor: pointer; font-size: 13px; color: #1e293b;
  transition: background 0.1s;
  &:hover { background: #f0fdf4; }
  &:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
}
.cm-city-name { font-weight: 500; }
.cm-city-code { font-size: 11px; color: #94a3b8; font-weight: 600; }

// SIRE / TRA (Colombia)
.cm-sire-notice {
  background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px;
  strong { display: block; font-size: 13px; font-weight: 700; color: #92400e; margin-bottom: 4px; }
  p { font-size: 12px; color: #78350f; margin: 0; line-height: 1.5; }
}
.cm-sire-summary {
  background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 10px;
  padding: 12px 16px; margin-top: 6px; margin-bottom: 14px;
  display: flex; flex-direction: column; gap: 6px;
}
.cm-sire-row {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 13px; color: #1e293b;
}
.cm-sire-label {
  font-weight: 700; color: #166534; white-space: nowrap; min-width: 100px;
}
.cm-sire-val {
  color: #1e293b;
}

// Photo upload
.cm-photo-upload {
  position: relative; border: 2px dashed #e2e8f0; border-radius: 10px;
  min-height: 100px; display: flex; align-items: center; justify-content: center;
  background: #f8fafc; overflow: hidden; transition: border-color 0.15s;
  &:hover:not(.has-photo) { border-color: #16a34a; background: #f0fdf4; }
  &.has-photo { border-style: solid; border-color: #bbf7d0; background: #fff; }
}
.cm-photo-label {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer; color: #94a3b8; font-size: 12px; font-weight: 600;
  padding: 16px; transition: color 0.15s;
  i { font-size: 22px; }
  &:hover { color: #16a34a; }
}
.cm-photo-preview {
  width: 100%; max-height: 140px; object-fit: cover; display: block;
}
.cm-photo-remove {
  position: absolute; top: 5px; right: 5px;
  width: 24px; height: 24px; border-radius: 50%; border: none;
  background: rgba(15,23,42,0.55); color: #fff; font-size: 12px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
  &:hover { background: #dc2626; }
}

// Firma
.cm-sig-box { border-radius: 10px; overflow: hidden; margin-bottom: 10px; }
.cm-sig-actions { display: flex; gap: 8px; margin-bottom: 14px; }
.cm-btn-ghost {
  padding: 6px 12px; border-radius: 8px; border: 1.5px solid #e2e8f0;
  background: #fff; color: #64748b; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  &:hover { background: #f1f5f9; border-color: #cbd5e1; color: #1e293b; }
}
.cm-terms-box {
  padding: 12px 14px; background: #f0fdf4; border-radius: 10px; border: 1px solid #bbf7d0;
}
.cm-chk-label {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 13px; color: #166534; cursor: pointer;
  input { margin-top: 2px; accent-color: #16a34a; flex-shrink: 0; }
}

// Directorio de Clientes
.cm-dir { margin-bottom: 14px; text-align: left; }
.cm-dir-applied {
  display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  font-size: 13px; color: #673de6; background: #f0ebfd; border-radius: 10px; padding: 8px 12px;
}
.cm-dir-missing {
  display: flex; align-items: flex-start; gap: 8px; margin-bottom: 10px;
  font-size: 12.5px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 8px 12px;
}
.cm-dir-search {
  position: relative;
  > i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #888; font-size: 14px; }
  .cm-input { padding-left: 34px; }
}
.cm-dir-list {
  position: absolute; left: 0; right: 0; top: calc(100% + 6px); z-index: 30;
  background: #fff; border-radius: 12px; max-height: 260px; overflow-y: auto;
  box-shadow: 0px 0px 20px 3px rgba(17, 17, 17, 0.08); padding: 6px;
}
.cm-dir-item {
  display: flex; flex-direction: column; align-items: flex-start; width: 100%;
  border: 0; background: transparent; border-radius: 8px; padding: 8px 10px; text-align: left; cursor: pointer;
  strong { font-size: 13.5px; color: #111; }
  small { font-size: 12px; color: #777; }
  &:hover { background: #f5f2fe; }
}
.cm-dir-empty { padding: 10px; font-size: 13px; color: #777; text-align: center; }
.cm-dir-match {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  border: 1px solid #d9cdfa; background: #f8f5ff; border-radius: 12px; padding: 12px 14px; margin-bottom: 14px; text-align: left;
}
.cm-dir-match__txt {
  display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1;
  strong { font-size: 14px; color: #673de6; }
  span { font-size: 13px; color: #111; }
  small { font-size: 12px; color: #777; }
}
.cm-dir-match__btns { display: flex; gap: 8px; flex-wrap: wrap; }
.cm-dir-btn {
  border: 1px solid #e3e3ea; background: #fff; color: #111; border-radius: 10px;
  height: 34px; padding: 0 12px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.cm-dir-btn--primary { background: #673de6; border-color: #673de6; color: #fff; }
.cm-dir-companions {
  border-radius: 12px; background: #f6f6f8; padding: 12px 14px; margin-bottom: 14px; text-align: left;
}
.cm-dir-companions--success { margin: 18px auto 0; max-width: 460px; width: 100%; }
.cm-dir-companions__head {
  display: flex; flex-direction: column; margin-bottom: 8px;
  strong { font-size: 13px; color: #111; }
  small { font-size: 12px; color: #777; }
}
.cm-dir-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.cm-dir-chip {
  display: inline-flex; align-items: center; gap: 6px; border: 1px solid #e3e3ea; background: #fff;
  border-radius: 999px; height: 32px; padding: 0 12px; font-size: 12.5px; font-weight: 600; color: #111; cursor: pointer;
  &:hover { border-color: #673de6; color: #673de6; }
}
.cm-dir-chip--strong { background: #111111; border-color: #111111; color: #fff; &:hover { background: #673de6; border-color: #673de6; color: #fff; } }

// Éxito
.cm-success {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 40px 22px; text-align: center;
}
.cm-success-icon { font-size: 56px; color: #16a34a; margin-bottom: 16px; }
.cm-success-title { font-size: 20px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
.cm-success-sub { font-size: 14px; color: #64748b; }

// Footer
.cm-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 22px; border-top: 1px solid #f1f5f9; flex-shrink: 0;
}
.cm-btn-cancel {
  padding: 9px 18px; border-radius: 10px; border: 1.5px solid #e2e8f0;
  background: #fff; color: #64748b; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  &:hover { background: #f1f5f9; }
  &:disabled { opacity: 0.5; cursor: default; }
}
.cm-btn-primary {
  padding: 9px 22px; border-radius: 10px; border: none;
  background: #16a34a; color: #fff; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: all 0.18s; box-shadow: 0 2px 8px rgba(22,163,74,0.25);
  &:hover { background: #15803d; }
  &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
}

// Transición
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

// Responsive
@media (max-width: 768px) {
  .manual-dispatch-card { flex-direction: column; align-items: stretch; }
  .btn-manual-dispatch,
  .manual-dispatch-chip { width: 100%; justify-content: center; }
  .checkin-item { flex-wrap: nowrap; gap: 4px; }
  .btn-action { width: 28px; height: 28px; font-size: 12px; border-radius: 8px; }
  .cm-row { flex-direction: column; gap: 10px; }
  .cm-dialog { border-radius: 14px; }
  .btn-new-checkin span { display: none; }
}

// ─── Italia — Alloggiati Web ────────────────────────────────────────────────
.cm-italy-banner {
  display: flex; align-items: flex-start; gap: 12px;
  margin: 14px 0 4px; padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f4ff, #e8f0fe);
  border: 1px solid #c7d7f9;
}
.cm-italy-flag { font-size: 22px; line-height: 1; flex-shrink: 0; }
.cm-italy-banner-txt {
  display: flex; flex-direction: column; gap: 2px;
  strong { font-size: 13px; color: #1e3a8a; }
  span   { font-size: 12px; color: #475569; line-height: 1.4; }
}

// vue-multiselect dentro del modal: altura/estilo coherentes con .cm-input
.cm-body {
  :deep(.multiselect) { min-height: 40px; font-size: 13px; color: #1e293b; }
  :deep(.multiselect__tags) {
    min-height: 40px; padding: 8px 40px 0 12px;
    border: 1.5px solid #e2e8f0; border-radius: 9px;
  }
  :deep(.multiselect--active .multiselect__tags) {
    border-color: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.10);
  }
  :deep(.multiselect__single),
  :deep(.multiselect__input) { font-size: 13px; color: #1e293b; margin-bottom: 0; padding-top: 0; }
  :deep(.multiselect__placeholder) { color: #94a3b8; font-size: 13px; padding-top: 0; margin-bottom: 0; }
  :deep(.multiselect__option--highlight) { background: #16a34a; }
  :deep(.multiselect__option--highlight::after) { background: #16a34a; }
  :deep(.multiselect__option--selected.multiselect__option--highlight) { background: #dc2626; }
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
